import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  FaTrash, 
  FaPlus, 
  FaCubes, 
  FaServer, 
  FaExclamationTriangle, 
  FaLink, 
  FaUnlink,
  FaTimes,
  FaCheck,
  FaCheckCircle,
  FaDatabase,
  FaInfoCircle,
  FaSync,
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaCopy,
  FaSearch,
  FaSort
} from 'react-icons/fa';
import { HardDrive } from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import CreateVolumeModal from './CreateVolumeModal';
import VolumeAttachModal from './VolumeAttachModal';
import Sidebar from '../../components/layout/Sidebar';
import { useResponsive } from '../../components/vm/useResponsive';
import { useNotification } from '../../Notifications';
import { useAuth } from '../../components/auth/AuthContext';

import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'volumes-page' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'volumes-page' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'volumes-page',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Security: Safe error messages mapping - Updated with backend error codes
const ERROR_MESSAGES = {
  // Authentication & Authorization
  'AUTH_ERROR': 'Please log in again to continue',
  'SESSION_EXPIRED': 'Your session has expired. Please log in again.',
  'USER_NOT_FOUND': 'Please refresh and try again',
  'INVALID_USER_PROFILE': 'Invalid user profile. Please contact support.',
  'ACCESS_DENIED': 'Access denied. Please check your permissions.',
  'INCOMPLETE_PROFILE': 'Please complete your profile before creating volumes.',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'INVALID_VOLUME_TYPE': 'Volume type not supported for this region. Please select a compatible volume type.',
  'FEATURE_NOT_SUPPORTED': 'This feature is not available for the selected region.',
  'VOLUME_STATUS_CHECK_FAILED': 'Unable to verify volume status. Please try again in a few moments.',

  // Volume Operations
  'VOLUME_LIST_FAILED': 'Unable to load volumes. Please refresh the page.',
  'VOLUME_CREATE_FAILED': 'Unable to create volume. Please try again.',
  'VOLUME_DELETE_FAILED': 'Unable to delete volume. Please try again.',
  'VOLUME_ATTACH_FAILED': 'Unable to attach volume. Please try again.',
  'VOLUME_DETACH_FAILED': 'Unable to detach volume. Please try again.',
  'VOLUME_RESIZE_FAILED': 'Unable to resize volume. Please try again.',
  'VOLUME_CLONE_FAILED': 'Unable to clone volume. Please try again.',
  'VOLUME_NOT_FOUND': 'Volume not found or you do not have permission to access it.',
  'VOLUME_IN_USE': 'Volume is currently attached to a VM and cannot be deleted. Please detach it first.',
  'VOLUME_SIZE_INVALID': 'Invalid volume size. Please check the size requirements.',
  'VOLUME_NOT_DETACHED': 'Volume must be detached before deletion',
  'BOOTABLE_VOLUME_NOT_AVAILABLE': 'Bootable volumes can only be deleted when available',
  'VOLUME_STATUS_NOT_AVAILABLE': 'Volume can only be deleted when available',
  'VOLUME_NOT_ATTACHED': 'Volume must be attached before detaching',
  'VOLUME_NOT_IN_USE': 'Volume must be in-use (attached) before detaching',
  'OS_VOLUME_DETACH_FORBIDDEN': 'OS/bootable volumes cannot be detached',
  'BOOTABLE_VOLUME_DETACH_FORBIDDEN': 'Bootable volumes cannot be detached',
  'CROSS_REGION_CLONE_NOT_SUPPORTED': 'Cross-region volume cloning is not supported',
  'SERVICE_ERROR': 'Service error. Please contact support.',
  // Volume Status & Cloning Issues
  'VOLUME_STATUS_INCONSISTENT': 'Volume status is inconsistent. Please wait a moment and try again.',
  'VOLUME_ATTACHED_TO_RUNNING_INSTANCE': 'Volume is attached to a running VM. Please shut down the VM before cloning.',
  'VOLUME_STATUS_NOT_CLONEABLE': 'Volume cannot be cloned in its current state. Please wait for any pending operations to complete.',
  // VM-Volume Interactions
  'VM_NOT_FOUND': 'VM not found or you do not have permission to access it.',
  'VM_NOT_SHUTOFF': 'VM must be in SHUTOFF state for volume operations.',
  'REGION_MISMATCH': 'Volume and VM must be in the same region for attachment.',

  // Region-specific limitations
  'UNSUPPORTED_OPERATION': 'This operation is not supported for this region.',
  'BOOTABLE_VOLUMES_NOT_SUPPORTED': 'Bootable volumes from OS images are not supported in this region.',

  // Resources & Data
  'RESOURCE_NOT_FOUND': 'Required resource not found. Please try a different region.',
  'RESOURCE_ALREADY_EXISTS': 'A volume with this name already exists. Please choose a different name.',
  'OS_IMAGES_FETCH_FAILED': 'Unable to load OS images. Please try again.',
  'VM_LIST_FAILED': 'Unable to load virtual machines. Please try again.',

  // Credits & Billing
  'INSUFFICIENT_CREDITS': 'Insufficient credits to create volume. Please add more credits.',

  // Validation & Input
  'INVALID_JSON': 'Invalid request format. Please try again.',
  'MISSING_FIELDS': 'Required information is missing. Please check your input.',
  'VALIDATION_ERROR': 'Invalid input data. Please check your entries.',
  'INVALID_ID_FORMAT': 'Invalid request. Please try again',
  'INVALID_DATA': 'Invalid input data',

  // System & Infrastructure  
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable',
  'API_CONNECTION_ERROR': 'Unable to connect to volume service. Please try again.',
  'DATABASE_ERROR': 'Service temporarily unavailable. Please try again later.',
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again later.',
  'DB_ERROR': 'Service temporarily unavailable. Please try again later.',
  'NETWORK_ERROR': 'Network connection failed. Please check your internet connection and try again.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',

  // Additional error codes
  'VOLUME_DETAILS_CHECK_FAILED': 'Unable to verify volume details. Please try again.',
  'INVALID_VM_ID_FORMAT': 'Invalid VM ID format. Please try again.',
  'CURRENCY_CONTEXT_MISMATCH': 'VM not available in current currency context. Please switch currencies.',
  'TOO_MANY_VOLUMES': 'Cannot attach/detach more than 10 volumes at once.',
  'DUPLICATE_VOLUMES': 'Duplicate volume IDs not allowed.',
  'MIXED_VOLUME_TIERS': 'All volumes must be from the same tier (legacy or advanced).',
  'RESOURCE_IN_USE': 'This resource is currently in use and cannot be modified.',
  'REGION_NOT_SUPPORTED': 'Selected region is not supported. Please choose a different region.',
  'INVALID_IMAGE_NAME': 'Invalid OS image selected. Please choose from available images.',
  'UNEXPECTED_PAYLOAD': 'Invalid request format. Please try again.',
  'REQUEST_TOO_LARGE': 'Request size too large. Please reduce the data size.',
  'INVALID_CURRENCY': 'Invalid currency. Supported currencies are USD, EUR, INR.',
  'COUNTRY_REQUIRED': 'Country is required. Please select your country.',
  'INVALID_REGION_GPU_COMBINATION': 'Invalid region and GPU type combination. Please check your selection.',
  'VM_NOT_FOUND_OR_FORBIDDEN': 'VM not found or you do not have permission to modify it.',
  'CROSS_TIER_NOT_ALLOWED': 'VM and volumes must be from the same tier.',
  'OPERATION_IN_PROGRESS': 'Volume attachment already in progress. Please wait.',

  // ID Resolution
  'ID_RESOLUTION_ERROR': 'Failed to resolve resource ID. Please try again.',

  // General
  'API_ERROR': 'Unable to complete the request. Please contact support.',
  // Profile & User Data
  'USER_AUTH_PROFILE_FAILED': 'Unable to load user profile. Please try again.',
  // Network Errors (6-position detection)
  'OFFLINE': "You're offline. Check your connection.",
  'TIMEOUT': "Request timed out. Please try again.",
  'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
  'NO_RESPONSE': "No response from server. Please try again.",
  'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
  'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again."
};

// State mismatch errors that require automatic volume refresh
const STATE_MISMATCH_ERRORS = new Set([
  'VOLUME_IN_USE',
  'VOLUME_NOT_FOUND',
  'VOLUME_NOT_DETACHED',
  'VM_NOT_SHUTOFF'
]);

// Helper function to check if error requires state resync
const isStateMismatchError = (errorCode) => STATE_MISMATCH_ERRORS.has(errorCode);

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Check specific codes FIRST
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Check HTTP status codes
  if (error.response?.status >= 500 || error.status >= 500) {
    return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
  }
  if (error.response?.status === 429 || error.status === 429) {
    const retryAfter = error.response?.headers?.['retry-after'] || 60;
    return `Too many requests. Please wait ${retryAfter} seconds before trying again.`;
  }
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['SESSION_EXPIRED'];
  }
  if (error.response?.status === 402 || error.status === 402) {
    return ERROR_MESSAGES['INSUFFICIENT_CREDITS'];
  }
  
  // Generic fallbacks LAST
  if (error.userMessage) {
    return error.userMessage;
  }
  if (error.error) {
    return error.error;
  }
  
  return 'Something went wrong. Please try again.';
};


// Network error detection with 6-position priority
const getNetworkErrorMessage = (error) => {
  // Position 2.1: Browser knows it's offline
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.2: Request timeout
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  // Position 2.3: Network error but browser thinks it's online
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  // Position 2.4: Network error and browser now offline (backup for 2.1)
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.5: Request sent but no response received
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  // Position 2.6: Fallback
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};
// Performance: Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Centralized currency utility
const currencyUtils = {
  getSymbol: (currencyCode) => {
    switch(currencyCode) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'INR': return '₹';
      default: return '$';
    }
  },
  defaultCurrency: 'USD'
};

// Region capability detection - Updated to match backend logic exactly
const getRegionCapabilities = (region) => {
  const advancedRegions = ['FINLAND-1', 'FINLAND-2', 'FINLAND-3'];
  const legacyRegions = ['CANADA-1'];
  
  return {
    supportsAdvancedOperations: advancedRegions.includes(region),
    supportsOSImages: legacyRegions.includes(region),
    isAdvancedRegion: advancedRegions.includes(region),
    isLegacyRegion: legacyRegions.includes(region)
  };
};

// Get volume display name (prioritize display_name for advanced regions)
const getVolumeDisplayName = (volume) => {
  return volume.display_name || volume.name || 'Unknown';
};

// Get volume type display - Updated to handle backend volume types
const getVolumeTypeDisplay = (volume) => {
  return volume.volume_type || volume.type || 'Standard';
};

const mapVolumeStatus = (volume) => {
  // Definitive attached states
  if (volume.instance_id || 
      volume.status === 'attached' || 
      volume.status === 'in-use') {
    return 'Attached';
  }
  
  // Definitive available states
  if (volume.status === 'detached' || 
      volume.status === 'available') {
    return 'Available';
  }
  
  return volume.status;
};

// VM status mapping with provider-specific normalization - Updated to match backend
const getVMStatus = (vm, volumeRegion) => {
  if (!vm || !vm.status) return null;
  const status = vm.status;
  return status;
};

// Check if volume supports advanced operations based on region
const supportsAdvancedOperations = (volume) => {
  const capabilities = getRegionCapabilities(volume.region);
  return capabilities.supportsAdvancedOperations;
};

// Helper function to check if volume can be deleted - Updated to match backend logic
const canDeleteVolume = (volume) => {
  const capabilities = getRegionCapabilities(volume.region);
  const volumeStatus = mapVolumeStatus(volume);
  
  if (capabilities.isLegacyRegion) {
    const isBootable = volume.bootable || false;
    
    if (isBootable) {
      return volumeStatus === 'Available';
    } else {
      return volumeStatus === 'Available';
    }
  } else {
    const isOSVolume = volume.bootable || volume.is_os_volume || false;
    
    if (isOSVolume) {
      return volumeStatus === 'Available';
    } else {
      return volumeStatus === 'Available';
    }
  }
};

// Helper function to check if volume can be detached - Updated to match backend logic
const canDetachVolume = (volume) => {
  const capabilities = getRegionCapabilities(volume.region);
  const volumeStatus = mapVolumeStatus(volume);
  const vmStatus = volume.vm_status;
  
  // Volume must be attached
  if (volumeStatus !== 'Attached') return false;
  
  if (capabilities.isLegacyRegion) {
    const isBootable = volume.bootable || false;
    
    if (isBootable) {
      return false;
    } else {
      return vmStatus === 'SHUTOFF';
    }
  } else {
    const isOSVolume = volume.bootable || volume.is_os_volume || false;
    
    if (isOSVolume) {
      return false;
    } else {
      return volumeStatus === 'Attached' && vmStatus === 'SHUTOFF';
    }
  }
};

const canAttachVolume = (volume) => {
  const volumeStatus = mapVolumeStatus(volume);
  return volumeStatus === 'Available';
};

// Get volume pricing - Updated to match backend structure
const getVolumePricing = (region, volumeType) => {
  const capabilities = getRegionCapabilities(region);
  
  if (capabilities.isLegacyRegion) {
    return { 'USD': 0.07129, 'EUR': 0.07129, 'INR': 7.12891 };
  } else {
    if (volumeType === 'HDD') {
      return { 'USD': 0.05703, 'EUR': 0.07129, 'INR': 5.70313 };
    } else if (volumeType === 'NVMe') {
      return { 'USD': 0.21387, 'EUR': 0.24951, 'INR': 21.38672 };
    } else {
      return { 'USD': 0.21387, 'EUR': 0.24951, 'INR': 21.38672 };
    }
  }
};

// Helper function to get filtered volume names based on region
const getFilteredVolumeNames = (volumes, selectedRegion) => {
  return volumes
    .filter(volume => volume.region === selectedRegion)
    .map(volume => getVolumeDisplayName(volume));
};

// Helper function to check if volume is an OS volume
const isOSVolume = (volume) => {
  return volume.is_os_volume === true || volume.bootable === true;
};

// Group pagination items per page
const GROUP_ITEMS_PER_PAGE = 15;

// Group Pagination Component
const GroupPagination = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  if (totalPages <= 1) return null;
  
  const startItem = (currentPage - 1) * GROUP_ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * GROUP_ITEMS_PER_PAGE, totalItems);
  
  return (
    <div className="flex justify-between items-center px-4 py-3 bg-[#141414] border-t border-[#2a2a2a]">
      <span className="text-xs text-gray-500">
        Showing {startItem}-{endItem} of {totalItems}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`w-7 h-7 rounded flex items-center justify-center border-none ${
            currentPage === 1 
              ? 'bg-[#1a1a1a] text-[#444] cursor-not-allowed' 
              : 'bg-[#333] text-white cursor-pointer hover:bg-[#444]'
          }`}
        >
          <FaChevronLeft size={10} />
        </button>
        
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`min-w-[28px] h-7 px-1.5 rounded border-none cursor-pointer text-xs ${
              currentPage === page 
                ? 'bg-[#e0eacc] text-black font-semibold' 
                : 'bg-[#252525] text-[#9ca3af] hover:bg-[#333]'
            }`}
          >
            {page}
          </button>
        ))}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`w-7 h-7 rounded flex items-center justify-center border-none ${
            currentPage === totalPages 
              ? 'bg-[#1a1a1a] text-[#444] cursor-not-allowed' 
              : 'bg-[#333] text-white cursor-pointer hover:bg-[#444]'
          }`}
        >
          <FaChevronRight size={10} />
        </button>
      </div>
    </div>
  );
};

// Volume Info Modal Component
const VolumeInfoModal = ({ isOpen, onClose, volume, onResize, onClone, isResizing, isCloning }) => {
  const { isMobile } = useResponsive();
  
  if (!isOpen || !volume) return null;

  const supportsAdvancedOps = supportsAdvancedOperations(volume);
  const volumeDisplayName = getVolumeDisplayName(volume);
  const volumeType = getVolumeTypeDisplay(volume);
  const volumeStatus = mapVolumeStatus(volume);
  const vmStatus = volume.vm_status;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className={`bg-[#121212] border border-[#333333] rounded-xl w-full shadow-md ${isMobile ? 'max-w-sm p-4' : 'max-w-md p-6'}`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`font-semibold text-[#e0eacc] ${isMobile ? 'text-base' : 'text-lg'}`}>Volume Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className="space-y-3 mb-6">
          <div className="flex justify-between">
            <span className="text-gray-400">ID:</span>
            <span className="text-white">{sanitizeText(volume.id)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Name:</span>
            <span className="text-white">{sanitizeText(volumeDisplayName)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Size:</span>
            <span className="text-white">{volume.size} GB</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Type:</span>
            <span className="text-white">{sanitizeText(volumeType)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Region:</span>
            <span className="text-white">{volume.region}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Status:</span>
            <span className="text-white">{volumeStatus}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Bootable:</span>
            <span className="text-white">{volume.bootable || volume.is_os_volume ? 'Yes' : 'No'}</span>
          </div>
          {volume.instance_id && (
            <div className="flex justify-between">
              <span className="text-gray-400">Attached to:</span>
              <span className="text-white">{sanitizeText(volume.instance_name) || `VM-${volume.instance_id.substring(0, 8)}`}</span>
            </div>
          )}
        </div>

        {supportsAdvancedOps && (
          <div className="border-t border-[#333333] pt-4 mb-4">
            <h4 className="text-white font-medium mb-3">Advanced Operations</h4>
            <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <button
                onClick={() => onResize(volume)}
                disabled={isResizing || !(volumeStatus === 'Attached' || volumeStatus === 'Available')}
                className={`flex items-center justify-center px-3 py-2 rounded-md text-sm transition-colors ${
                  (isResizing || !(volumeStatus === 'Attached' || volumeStatus === 'Available'))
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]'
                }`}
                title={
                  !(volumeStatus === 'Attached' || volumeStatus === 'Available')
                    ? 'Volume can only be resized when attached or available'
                    : 'Resize Volume'
                }
              >
                {isResizing ? (
                  <div className="flex items-center">
                    <div className="animate-spin h-3 w-3 border-2 border-t-black border-black border-opacity-20 rounded-full mr-2"></div>
                    <span>Resizing...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <FaExpand size={12} className="mr-2" />
                    <span>Resize</span>
                  </div>
                )}
              </button>
              
              <button
                onClick={() => onClone(volume)}
                disabled={isCloning || !(volumeStatus === 'Available' || (volumeStatus === 'Attached' && vmStatus === 'SHUTOFF'))}
                className={`flex items-center justify-center px-3 py-2 rounded-md text-sm transition-colors ${
                  (isCloning || !(volumeStatus === 'Available' || (volumeStatus === 'Attached' && vmStatus === 'SHUTOFF')))
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]'
                }`}
                title={
                  !(volumeStatus === 'Available' || (volumeStatus === 'Attached' && vmStatus === 'SHUTOFF'))
                    ? 'Volume can only be cloned when available or attached to a shut off VM'
                    : 'Clone Volume'
                }
              >
                {isCloning ? (
                  <div className="flex items-center">
                    <div className="animate-spin h-3 w-3 border-2 border-t-black border-black border-opacity-20 rounded-full mr-2"></div>
                    <span>Cloning...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <FaCopy size={12} className="mr-2" />
                    <span>Clone</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className={`bg-[#333333] hover:bg-[#444444] text-white rounded-md transition-colors ${isMobile ? 'py-3 px-4 text-sm w-full' : 'px-4 py-2'}`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const VolumesPage = () => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();

  const [isDeleting, setIsDeleting] = useState(false);
  
  // Performance: Initial load tracking
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  // Performance: Granular loading states
  const [loadingStates, setLoadingStates] = useState({
    volumes: false,
    vms: false,
    osImages: false,
    userProfile: false,
    creating: false,
    deleting: new Set(),
    attaching: new Set(),
    detaching: new Set(),
    cloning: new Set(),
    resizing: new Set()
  });

  const [volumes, setVolumes] = useState([]);
  const [vms, setVms] = useState([]);
  const [error, setError] = useState(null);
const [regions] = useState(['CANADA-1', 'FINLAND-1', 'FINLAND-2', 'FINLAND-3']);
  const [osImages, setOsImages] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('CANADA-1');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneSourceVolume, setCloneSourceVolume] = useState(null);
  const [resizeModalOpen, setResizeModalOpen] = useState(false);
  const [resizeSourceVolume, setResizeSourceVolume] = useState(null);
  const [nextVolumeName, setNextVolumeName] = useState('');

  const [statusMessage, setStatusMessage] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, volume: null });
  const [attachModal, setAttachModal] = useState({ open: false, volume: null });
  const [detachConfirmModal, setDetachConfirmModal] = useState({ open: false, volume: null });
  const [infoModal, setInfoModal] = useState({ open: false, volume: null });
  const [attachedVolumeMap, setAttachedVolumeMap] = useState({});
  const [vmStatusMap, setVmStatusMap] = useState({});
  const [lockedVolumes, setLockedVolumes] = useState(new Set());
  
  // Profile and balance validation states
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [hasMinimumBalance, setHasMinimumBalance] = useState(true);
  const [userBalance, setUserBalance] = useState({ usd: 0, eur: 0, inr: 0 });
  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  
  // Dynamic pricing state
  const [volumePricing, setVolumePricing] = useState({ 'USD': 0.1, 'EUR': 0.1, 'INR': 10 });

  // NEW: Grouped list view states
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [expandedVolumeId, setExpandedVolumeId] = useState(null);
  const [groupPages, setGroupPages] = useState({ 'In Progress': 1, Available: 1, Attached: 1 });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  
  // Performance: Request cancellation
  const abortControllerRef = useRef(null);
  
  const operationRefs = useRef({
    attaching: new Set(),
    detaching: new Set(),
    cloning: new Set(),
    resizing: new Set(),
    deleting: new Set()
  });

  // Performance: Request rate control - retry limits
  const maxRetries = 3;
  const retryAttempts = useRef(0);
  
  const regionDropdownRef = useRef(null);
  
  const navigate = useNavigate();

  // Sort options for dropdown
  const sortOptions = [
    { id: 'name', label: 'Name' },
    { id: 'size', label: 'Size' },
    { id: 'created', label: 'Date' },
  ];

  // Copy to clipboard helper
  const copyToClipboard = useCallback((id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    
    logUserAction('copy_volume_id', { volumeId: id });
  }, []);

  // Toggle group collapse
  const toggleGroup = useCallback((groupName) => {
    setCollapsedGroups(prev => ({ 
      ...prev, 
      [groupName]: !prev[groupName] 
    }));
    
    logUserAction('toggle_volume_group', { 
      groupName, 
      collapsed: !collapsedGroups[groupName] 
    });
  }, [collapsedGroups]);

  // Handle group page change
  const handleGroupPageChange = useCallback((groupName, page) => {
    setGroupPages(prev => ({ ...prev, [groupName]: page }));
    
    logUserAction('group_page_change', { groupName, page });
  }, []);

  // Check profile completeness
  const checkProfileCompleteness = useCallback(async () => {
    try {
      const response = await api.getUserProfile();
      if (response && response.success && response.profile) {
        setIsProfileComplete(response.profile.is_profile_complete || false);
      }
    } catch (error) {
      logError('Failed to check profile completeness', { error: error.message });
    }
  }, []);
  
  // Check minimum balance
  const checkMinimumBalance = useCallback(async () => {
    try {
      const response = await api.getCreditBalance();
      if (response && response.success) {
        const balances = response.available_balance || response.balance;
        const currency = response.account_currency || 'USD';
        
        setUserBalance(balances);
        setPreferredCurrency(currency);
        
        const minimums = { USD: 1, EUR: 1, INR: 100 };
        const requiredAmount = minimums[currency] || 1;
        const currentBalance = balances[currency.toLowerCase()] || 0;
        
        setHasMinimumBalance(currentBalance >= requiredAmount);
      }
    } catch (error) {
      logError('Failed to check minimum balance', { error: error.message });
    }
  }, []);

  const fetchVolumeTypesAndSetPricing = useCallback(async (region, volumeType) => {
    try {
      const pricing = getVolumePricing(region, volumeType);
      
      setVolumePricing(pricing);
      
      logInfo('Pricing set based on region', {
        region,
        volumeType,
        pricing
      });
    } catch (error) {
      const defaultPricing = { 'USD': 0.07129, 'EUR': 0.07129, 'INR': 7.12891 };
      setVolumePricing(defaultPricing);
      
      logError('Failed to set pricing, using default', {
        region,
        fallbackPricing: defaultPricing,
        error: error.message
      });
    }
  }, []);

  // Check validations on component mount
  useEffect(() => {
    checkProfileCompleteness();
    checkMinimumBalance();
  }, [checkProfileCompleteness, checkMinimumBalance]);

  // Get button tooltip message
  const getCreateButtonTooltip = useMemo(() => {
    if (!isProfileComplete) {
      return "Please complete your profile before creating volumes";
    }
    if (!hasMinimumBalance) {
      const minimumBalances = { USD: 1.0, EUR: 1.0, INR: 100.0 };
      const minRequired = minimumBalances[preferredCurrency] || 1.0;
      const symbol = currencyUtils.getSymbol(preferredCurrency);
      return `Minimum balance of ${symbol}${minRequired} ${preferredCurrency} required to create volumes`;
    }
    return "Create a new volume";
  }, [isProfileComplete, hasMinimumBalance, preferredCurrency]);

  // Set user context for Sentry
  useEffect(() => {
    Sentry.setContext("volumes-page", {
      volumeCount: volumes.length,
      selectedRegion,
      isMobile,
      isTablet
    });
  }, [volumes.length, selectedRegion, isMobile, isTablet]);

  // Performance: Request cancellation cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logInfo('Cleanup: Aborted pending volume requests');
      }
    };
  }, []);

  // NEW: Filtered and sorted volumes
  const filteredVolumes = useMemo(() => {
    let result = volumes.filter(v => v.region === selectedRegion);
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(v => 
        getVolumeDisplayName(v).toLowerCase().includes(query) ||
        v.id.toLowerCase().includes(query)
      );
    }
    
    result = [...result].sort((a, b) => {
      if (sortBy === 'name') {
        return getVolumeDisplayName(a).localeCompare(getVolumeDisplayName(b));
      }
      if (sortBy === 'size') {
        return b.size - a.size;
      }
      if (sortBy === 'created') {
        const dateA = new Date(a.created_at || a.created || 0);
        const dateB = new Date(b.created_at || b.created || 0);
        return dateB - dateA;
      }
      return 0;
    });
    
    return result;
  }, [volumes, selectedRegion, searchQuery, sortBy]);

  const groupedVolumes = useMemo(() => {
    const groups = { 'In Progress': [], Available: [], Attached: [] };
    
    filteredVolumes.forEach(volume => {
      const status = mapVolumeStatus(volume);
      if (status === 'Available') {
        groups.Available.push(volume);
      } else if (status === 'Attached') {
        groups.Attached.push(volume);
      } else {
        groups['In Progress'].push(volume);
      }
    });
    
    logInfo('Volumes grouped by status', {
      inProgressCount: groups['In Progress'].length,
      availableCount: groups.Available.length,
      attachedCount: groups.Attached.length,
      totalFiltered: filteredVolumes.length
    });
    
    return groups;
  }, [filteredVolumes]);

  // NEW: Paginated volumes per group
  const getPaginatedGroupVolumes = useCallback((groupName) => {
    const groupVols = groupedVolumes[groupName] || [];
    const currentPage = groupPages[groupName] || 1;
    const startIndex = (currentPage - 1) * GROUP_ITEMS_PER_PAGE;
    const endIndex = startIndex + GROUP_ITEMS_PER_PAGE;
    return groupVols.slice(startIndex, endIndex);
  }, [groupedVolumes, groupPages]);

  // NEW: Get total pages for a group
  const getGroupTotalPages = useCallback((groupName) => {
    const groupVols = groupedVolumes[groupName] || [];
    return Math.ceil(groupVols.length / GROUP_ITEMS_PER_PAGE);
  }, [groupedVolumes]);

// ============================================================================
// FIXED: debouncedFetchVolumes
// ============================================================================
const debouncedFetchVolumes = useCallback(
  debounce(async () => {
    const startTime = performance.now();
    
    logInfo('Initiating volume fetch request', {
      selectedRegion
    });

    if (retryAttempts.current >= maxRetries) {
      setError('Maximum refresh attempts reached. Please wait before trying again.');
      logError('Maximum volume fetch attempts exceeded', {
        attempts: retryAttempts.current,
        maxRetries,
        selectedRegion
      });
      return;
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    
    retryAttempts.current++;
    setLoadingStates(prev => ({ ...prev, volumes: true }));
    setError(null);
    
    try {
      const response = await api.getVolumesWithNextName({ region: selectedRegion });
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (abortControllerRef.current?.signal.aborted) {
        logInfo('Volume fetch request was aborted', { duration });
        return;
      }

      if (response.success) {
        const allVolumes = response.volumes || [];
        
        if (response.next_volume_name) {
          setNextVolumeName(response.next_volume_name);
        }
        
        setVolumes(allVolumes);
        
        if (!initialDataLoaded) setInitialDataLoaded(true);
        retryAttempts.current = 0;

        logPerformance('fetch_volumes', duration, {
          volumeCount: allVolumes.length,
          selectedRegion,
          success: true
        });

        logInfo('Volumes loaded successfully', {
          count: allVolumes.length,
          nextVolumeName: response.next_volume_name,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);
        
        logError('Failed to fetch volumes', {
          response: response || 'No response',
          selectedRegion,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        logInfo('Volume fetch request aborted by user');
        return;
      }
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401) {
        handleAuthError();
        setLoadingStates(prev => ({ ...prev, volumes: false }));
        return;
      }
      
      // Position 2: Network error
      const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
      if (networkErrorCodes.includes(err.code)) {
        setError(err.error);
        logError('Network error in debouncedFetchVolumes', { 
          code: err.code, 
          duration: `${duration.toFixed(2)}ms`,
          selectedRegion
        }, err);
        setLoadingStates(prev => ({ ...prev, volumes: false }));
        return;
      }
      
      // Position 3: Rate limit
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setError(err.error);
        setLoadingStates(prev => ({ ...prev, volumes: false }));
        return;
      }
      
      // Position 4: Mapped error code
      if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        logError('Volumes fetch error - mapped code', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setLoadingStates(prev => ({ ...prev, volumes: false }));
        return;
      }

      // Position 5: Fallback
      const safeMessage = err.error || getSafeErrorMessage(err);
      setError(safeMessage);

      logError('Error fetching volumes', {
        errorName: err.name,
        selectedRegion,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, volumes: false }));
    }
  }, 300),
  [selectedRegion, handleAuthError]
);


// ============================================================================
// FIXED: debouncedFetchVMs
// ============================================================================
const debouncedFetchVMs = useCallback(
  debounce(async (region) => {
    const startTime = performance.now();
    
    logInfo('Initiating VM fetch request', { region });
    
    setLoadingStates(prev => ({ ...prev, vms: true }));
    
    try {
      const response = await api.getVMs();
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (response.success) {
        const getVMRegion = (vm) => {
          const vmRegion = vm.region || vm.availability_zone || vm.zone || null;
          
          const locationToRegion = {
            'FIN-01': 'FINLAND-1',
            'FIN-02': 'FINLAND-2', 
            'FIN-03': 'FINLAND-3',
            'Finland 1': 'FINLAND-1',
            'Finland 2': 'FINLAND-2',
            'Finland 3': 'FINLAND-3',
            'Canada 1': 'CANADA-1'
          };
          
          return locationToRegion[vmRegion] || vmRegion;
        };

        const regionVMs = (response.instances || [])
          .filter(vm => getVMRegion(vm) === region);
        setVms(regionVMs);
        
        const statusMap = {};
        response.instances.forEach(vm => {
          statusMap[vm.instance_id || vm.server_uuid] = vm.status;
        });
        setVmStatusMap(statusMap);

        logPerformance('fetch_vms', duration, {
          region,
          totalVMs: (response.instances || []).length,
          regionVMs: regionVMs.length,
          success: true
        });

        logInfo('VMs loaded successfully', {
          region,
          totalVMs: (response.instances || []).length,
          regionVMs: regionVMs.length,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);
        
        logError('Failed to fetch VMs', {
          region,
          response: response || 'No response',
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (err) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401) {
        handleAuthError();
        setLoadingStates(prev => ({ ...prev, vms: false }));
        return;
      }
      
      // Position 2: Network error
      const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
      if (networkErrorCodes.includes(err.code)) {
        setError(err.error);
        logError('Network error in debouncedFetchVMs', { 
          code: err.code, 
          duration: `${duration.toFixed(2)}ms`,
          region
        }, err);
        setLoadingStates(prev => ({ ...prev, vms: false }));
        return;
      }
      
      // Position 3: Rate limit
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setError(err.error);
        setLoadingStates(prev => ({ ...prev, vms: false }));
        return;
      }
      
      // Position 4: Mapped error code
      if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        logError('VMs fetch error - mapped code', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setLoadingStates(prev => ({ ...prev, vms: false }));
        return;
      }
      
      // Position 5: Fallback
      const safeMessage = err.error || getSafeErrorMessage(err);
      setError(safeMessage);
      
      logError('Error fetching VMs', {
        region,
        errorName: err.name,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, vms: false }));
    }
  }, 300),
  [handleAuthError]
);


// ============================================================================
// FIXED: debouncedFetchOsImages
// ============================================================================
const debouncedFetchOsImages = useCallback(
  debounce(async (region) => {
    const startTime = performance.now();
    
    logInfo('Initiating OS images fetch request', { region });
    
    setLoadingStates(prev => ({ ...prev, osImages: true }));
    
    try {
      const capabilities = getRegionCapabilities(region);
      
      if (!capabilities.supportsOSImages) {
        setOsImages([]);
        logInfo('OS images not supported for this region', { region });
        setLoadingStates(prev => ({ ...prev, osImages: false }));
        return;
      }
      
      const response = await api.getImages(region);
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (response.success) {
        const fetchedImages = response.images || [];
        const ubuntuImages = fetchedImages.filter(img => 
          img.name && img.name.toLowerCase().includes('ubuntu')
        );
        setOsImages(ubuntuImages);

        logPerformance('fetch_os_images', duration, {
          region,
          imageCount: ubuntuImages.length,
          success: true
        });

        logInfo('OS images loaded successfully', {
          region,
          imageCount: ubuntuImages.length,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);
        setOsImages([]);
        
        logError('Failed to fetch OS images', {
          region,
          response: response || 'No response',
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (err) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401) {
        handleAuthError();
        setOsImages([]);
        setLoadingStates(prev => ({ ...prev, osImages: false }));
        return;
      }
      
      // Position 2: Network error
      const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
      if (networkErrorCodes.includes(err.code)) {
        setError(err.error);
        logError('Network error in debouncedFetchOsImages', { 
          code: err.code, 
          duration: `${duration.toFixed(2)}ms`,
          region
        }, err);
        setOsImages([]);
        setLoadingStates(prev => ({ ...prev, osImages: false }));
        return;
      }
      
      // Position 3: Rate limit
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setError(err.error);
        setOsImages([]);
        setLoadingStates(prev => ({ ...prev, osImages: false }));
        return;
      }
      
      // Position 4: Mapped error code
      if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        logError('OS images fetch error - mapped code', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setOsImages([]);
        setLoadingStates(prev => ({ ...prev, osImages: false }));
        return;
      }
      
      // Position 5: Fallback
      const safeMessage = err.error || getSafeErrorMessage(err);
      setError(safeMessage);
      setOsImages([]);
      
      logError('Error fetching OS images', {
        region,
        errorName: err.name,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, osImages: false }));
    }
  }, 300),
  [handleAuthError]
);


// ============================================================================
// FIXED: debouncedFetchCreditBalance
// ============================================================================
const debouncedFetchCreditBalance = useCallback(
  debounce(async () => {
    const startTime = performance.now();
    
    logInfo('Initiating credit balance fetch request');
    
    try {
      const response = await api.getCreditBalance();
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (response && response.success) {
        const balances = response.available_balance || response.balance;
        const currency = response.account_currency || 'USD';
        
        setUserBalance(balances);
        setPreferredCurrency(currency);
        
        const minimums = { USD: 1, EUR: 1, INR: 100 };
        const requiredAmount = minimums[currency] || 1;
        const currentBalance = balances[currency.toLowerCase()] || 0;
        
        setHasMinimumBalance(currentBalance >= requiredAmount);

        logPerformance('fetch_credit_balance', duration, {
          currency,
          hasMinimumBalance: currentBalance >= requiredAmount,
          success: true
        });

        logInfo('Credit balance loaded successfully', {
          currency,
          hasMinimumBalance: currentBalance >= requiredAmount,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        logInfo('Credit balance fetch completed with default', {
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (err) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401) {
        handleAuthError();
        return;
      }
      
      // Position 2: Network error (silent fail with logging)
      const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
      if (networkErrorCodes.includes(err.code)) {
        logError('Network error in debouncedFetchCreditBalance', { 
          code: err.code, 
          duration: `${duration.toFixed(2)}ms`
        }, err);
        // Silent fail - will use default balance
        return;
      }
      
      // Position 3: Rate limit (silent fail with logging)
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        logError('Rate limited in debouncedFetchCreditBalance', {
          duration: `${duration.toFixed(2)}ms`
        }, err);
        // Silent fail - will use default balance
        return;
      }
      
      // Position 4: Mapped error code (silent fail with logging)
      if (err.code && ERROR_MESSAGES[err.code]) {
        logError('Credit balance fetch error - mapped code', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 5: Fallback (silent fail with logging)
      logError('Error fetching credit balance', {
        errorName: err.name,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    }
  }, 300),
  [handleAuthError]
);

  // Fetch volumes on component mount and when region changes
  useEffect(() => {
    logInfo('VolumesPage component mounted', {
      isMobile,
      isTablet,
      selectedRegion
    });
    
    debouncedFetchVolumes();
  }, [selectedRegion, debouncedFetchVolumes]);
  
  // Fetch OS images and pricing when region changes
  useEffect(() => {
    if (selectedRegion) {
      debouncedFetchOsImages(selectedRegion);
      fetchVolumeTypesAndSetPricing(selectedRegion, 'NVMe');
    }
  }, [selectedRegion, debouncedFetchOsImages, fetchVolumeTypesAndSetPricing]);
  
  // Handle click outside for region dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
        setIsRegionDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setGroupPages({ 'In Progress': 1, Available: 1, Attached: 1 });
    setExpandedVolumeId(null);
  }, [selectedRegion, searchQuery, sortBy]);

const handleCreateVolume = useCallback(async (volumeData) => {
  const startTime = performance.now();
  
  logUserAction('create_volume_initiated', {
    volumeName: volumeData.name,
    size: volumeData.size,
    region: volumeData.region
  });
  
  logInfo('Starting volume creation', {
    volumeData: {
      name: volumeData.name,
      size: volumeData.size,
      region: volumeData.region,
      bootable: volumeData.bootable
    }
  });
  
  setLoadingStates(prev => ({ ...prev, creating: true }));
  
  try {
    const response = await api.createVolume(volumeData);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success) {
      setCreateModalOpen(false);
      notification.success('Volume created successfully');
      debouncedFetchVolumes();

      logPerformance('create_volume', duration, {
        volumeName: volumeData.name,
        size: volumeData.size,
        region: volumeData.region,
        success: true
      });

      logInfo('Volume created successfully', {
        volumeName: volumeData.name,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('create_volume_success', {
        volumeName: volumeData.name,
        volumeId: response.volume_id || 'pending'
      });
      
      return response;
    } else {
      const safeMessage = response.error || 
                          ERROR_MESSAGES[response.code] || 
                          'Volume creation failed. Please try again.';
      notification.error(safeMessage);
      
      logError('Failed to create volume', {
        volumeName: volumeData.name,
        response: response || 'No response',
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('create_volume_failed', {
        volumeName: volumeData.name
      });
      
      return response;
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401) {
      handleAuthError();
      setLoadingStates(prev => ({ ...prev, creating: false }));
      return { success: false };
    }
    
    // Position 2: Network error
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      notification.error(err.error);
      logError('Network error in handleCreateVolume', { 
        code: err.code, 
        duration: `${duration.toFixed(2)}ms`,
        volumeName: volumeData.name
      }, err);
      setLoadingStates(prev => ({ ...prev, creating: false }));
      return { success: false, error: err.error };
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      notification.error(err.error);
      setLoadingStates(prev => ({ ...prev, creating: false }));
      return { success: false, error: err.error };
    }
    
    // Position 4: Mapped error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      const safeMessage = ERROR_MESSAGES[err.code];
      notification.error(safeMessage);
      logError('Create volume error - mapped code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setLoadingStates(prev => ({ ...prev, creating: false }));
      return { success: false, error: safeMessage };
    }

    // Position 5: Fallback
    const safeMessage = err.error || getSafeErrorMessage(err);
    notification.error(safeMessage);

    logError('Error creating volume', {
      volumeName: volumeData.name,
      errorName: err.name,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('create_volume_error', {
      volumeName: volumeData.name
    });
    
    return { success: false, error: safeMessage };
  } finally {
    setLoadingStates(prev => ({ ...prev, creating: false }));
  }
}, [debouncedFetchVolumes, handleAuthError, notification]);

const handleCloneVolume = useCallback(async (sourceVolumeId, cloneName, targetRegion, volumeType) => {
  if (operationRefs.current.cloning.has(sourceVolumeId)) {
    return;
  }
  
  const startTime = performance.now();
  
  operationRefs.current.cloning.add(sourceVolumeId);
  
  logUserAction('clone_volume_initiated', {
    sourceVolumeId,
    cloneName,
    targetRegion,
    volumeType
  });
  
  logInfo('Starting volume clone', {
    sourceVolumeId,
    cloneName,
    targetRegion,
    volumeType
  });
  
  setLoadingStates(prev => ({ 
    ...prev, 
    cloning: new Set([...prev.cloning, sourceVolumeId])
  }));
  
  setLockedVolumes(prev => new Set([...prev, sourceVolumeId]));
  setTimeout(() => {
    setLockedVolumes(prev => {
      const newLocked = new Set(prev);
      newLocked.delete(sourceVolumeId);
      return newLocked;
    });
  }, 3000);
  
  try {
    const response = await api.cloneVolume(sourceVolumeId, cloneName, targetRegion, volumeType);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success) {
      setCloneModalOpen(false);
      setCloneSourceVolume(null);
      notification.success('Volume cloned successfully');
      debouncedFetchVolumes();

      logPerformance('clone_volume', duration, {
        sourceVolumeId,
        cloneName,
        targetRegion,
        success: true
      });

      logInfo('Volume cloned successfully', {
        sourceVolumeId,
        cloneName,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('clone_volume_success', {
        sourceVolumeId,
        cloneName
      });
      
      return response;
    } else {
      const safeMessage = response.error || 
                          ERROR_MESSAGES[response.code] || 
                          'Volume cloning failed. Please try again.';
      notification.error(safeMessage);
      
      logError('Failed to clone volume', {
        sourceVolumeId,
        cloneName,
        response: response || 'No response',
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('clone_volume_failed', {
        sourceVolumeId,
        cloneName
      });
      
      return response;
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401) {
      handleAuthError();
      operationRefs.current.cloning.delete(sourceVolumeId);
      setLoadingStates(prev => {
        const newCloning = new Set(prev.cloning);
        newCloning.delete(sourceVolumeId);
        return { ...prev, cloning: newCloning };
      });
      return { success: false };
    }
    
    // Position 2: Network error
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      notification.error(err.error);
      logError('Network error in handleCloneVolume', { 
        code: err.code, 
        duration: `${duration.toFixed(2)}ms`,
        sourceVolumeId,
        cloneName
      }, err);
      operationRefs.current.cloning.delete(sourceVolumeId);
      setLoadingStates(prev => {
        const newCloning = new Set(prev.cloning);
        newCloning.delete(sourceVolumeId);
        return { ...prev, cloning: newCloning };
      });
      return { success: false, error: err.error };
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      notification.error(err.error);
      operationRefs.current.cloning.delete(sourceVolumeId);
      setLoadingStates(prev => {
        const newCloning = new Set(prev.cloning);
        newCloning.delete(sourceVolumeId);
        return { ...prev, cloning: newCloning };
      });
      return { success: false, error: err.error };
    }
    
    // Position 4: Mapped error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      const safeMessage = ERROR_MESSAGES[err.code];
      notification.error(safeMessage);
      logError('Clone volume error - mapped code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      operationRefs.current.cloning.delete(sourceVolumeId);
      setLoadingStates(prev => {
        const newCloning = new Set(prev.cloning);
        newCloning.delete(sourceVolumeId);
        return { ...prev, cloning: newCloning };
      });
      return { success: false, error: safeMessage };
    }

    // Position 5: Fallback
    const safeMessage = err.error || getSafeErrorMessage(err);
    notification.error(safeMessage);

    logError('Error cloning volume', {
      sourceVolumeId,
      cloneName,
      errorName: err.name,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('clone_volume_error', {
      sourceVolumeId,
      cloneName
    });
    
    return { success: false, error: safeMessage };
  } finally {
    operationRefs.current.cloning.delete(sourceVolumeId);
    setLoadingStates(prev => {
      const newCloning = new Set(prev.cloning);
      newCloning.delete(sourceVolumeId);
      return { ...prev, cloning: newCloning };
    });
  }
}, [debouncedFetchVolumes, handleAuthError, notification]);

const handleResizeVolume = useCallback(async (volumeId, newSize) => {
  if (operationRefs.current.resizing.has(volumeId)) {
    return;
  }
  
  const startTime = performance.now();
  
  operationRefs.current.resizing.add(volumeId);
  
  logUserAction('resize_volume_initiated', {
    volumeId,
    newSize
  });
  
  logInfo('Starting volume resize', {
    volumeId,
    newSize
  });
  
  setLoadingStates(prev => ({ 
    ...prev, 
    resizing: new Set([...prev.resizing, volumeId])
  }));
  
  setLockedVolumes(prev => new Set([...prev, volumeId]));
  setTimeout(() => {
    setLockedVolumes(prev => {
      const newLocked = new Set(prev);
      newLocked.delete(volumeId);
      return newLocked;
    });
  }, 3000);
  
  try {
    const response = await api.resizeVolume(volumeId, newSize);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success) {
      setResizeModalOpen(false);
      setResizeSourceVolume(null);
      notification.success('Volume resized successfully');
      debouncedFetchVolumes();

      logPerformance('resize_volume', duration, {
        volumeId,
        newSize,
        success: true
      });

      logInfo('Volume resized successfully', {
        volumeId,
        newSize,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('resize_volume_success', {
        volumeId,
        newSize
      });
      
      return response;
    } else {
      const safeMessage = response.error || 
                          ERROR_MESSAGES[response.code] || 
                          'Volume resize failed. Please try again.';
      notification.error(safeMessage);
      
      logError('Failed to resize volume', {
        volumeId,
        newSize,
        response: response || 'No response',
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('resize_volume_failed', {
        volumeId,
        newSize
      });
      
      return response;
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401) {
      handleAuthError();
      operationRefs.current.resizing.delete(volumeId);
      setLoadingStates(prev => {
        const newResizing = new Set(prev.resizing);
        newResizing.delete(volumeId);
        return { ...prev, resizing: newResizing };
      });
      return { success: false };
    }
    
    // Position 2: Network error
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      notification.error(err.error);
      logError('Network error in handleResizeVolume', { 
        code: err.code, 
        duration: `${duration.toFixed(2)}ms`,
        volumeId,
        newSize
      }, err);
      operationRefs.current.resizing.delete(volumeId);
      setLoadingStates(prev => {
        const newResizing = new Set(prev.resizing);
        newResizing.delete(volumeId);
        return { ...prev, resizing: newResizing };
      });
      return { success: false, error: err.error };
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      notification.error(err.error);
      operationRefs.current.resizing.delete(volumeId);
      setLoadingStates(prev => {
        const newResizing = new Set(prev.resizing);
        newResizing.delete(volumeId);
        return { ...prev, resizing: newResizing };
      });
      return { success: false, error: err.error };
    }
    
    // Position 4: Mapped error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      const safeMessage = ERROR_MESSAGES[err.code];
      notification.error(safeMessage);
      logError('Resize volume error - mapped code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      operationRefs.current.resizing.delete(volumeId);
      setLoadingStates(prev => {
        const newResizing = new Set(prev.resizing);
        newResizing.delete(volumeId);
        return { ...prev, resizing: newResizing };
      });
      return { success: false, error: safeMessage };
    }

    // Position 5: Fallback
    const safeMessage = err.error || getSafeErrorMessage(err);
    notification.error(safeMessage);

    logError('Error resizing volume', {
      volumeId,
      newSize,
      errorName: err.name,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('resize_volume_error', {
      volumeId,
      newSize
    });
    
    return { success: false, error: safeMessage };
  } finally {
    operationRefs.current.resizing.delete(volumeId);
    setLoadingStates(prev => {
      const newResizing = new Set(prev.resizing);
      newResizing.delete(volumeId);
      return { ...prev, resizing: newResizing };
    });
  }
}, [debouncedFetchVolumes, handleAuthError, notification]);
const handleDeleteClick = useCallback((volume) => {
    if (operationRefs.current.deleting.has(volume.id) || loadingStates.deleting.has(volume.id)) {
      return;
    }
    
    logUserAction('delete_volume_modal_opened', {
      volumeId: volume.id,
      volumeName: getVolumeDisplayName(volume)
    });
    
    logInfo('Opening delete volume modal', {
      volumeId: volume.id,
      volumeName: getVolumeDisplayName(volume),
      volumeStatus: mapVolumeStatus(volume)
    });
    
    setDeleteModal({
      open: true,
      volume: volume
    });
  }, [loadingStates.deleting]);

const handleDeleteConfirm = useCallback(async () => {
  if (!deleteModal.volume) return;
  
  const volumeId = deleteModal.volume.id;
  const volumeName = getVolumeDisplayName(deleteModal.volume);
  
  operationRefs.current.deleting.add(volumeId);
  setLoadingStates(prev => ({ 
    ...prev, 
    deleting: new Set([...prev.deleting, volumeId])
  }));
  
  setIsDeleting(true);
  
  const startTime = performance.now();
  
  logUserAction('delete_volume_confirmed', {
    volumeId,
    volumeName
  });
  
  logInfo('Starting volume deletion', {
    volumeId,
    volumeName
  });
    
  try {
    const response = await api.deleteVolume(volumeId);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success) {
      notification.success('Volume deleted successfully');
      debouncedFetchVolumes();

      logPerformance('delete_volume', duration, {
        volumeId,
        volumeName,
        success: true
      });

      logInfo('Volume deleted successfully', {
        volumeId,
        volumeName,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('delete_volume_success', {
        volumeId,
        volumeName
      });
    } else {
      const safeMessage = response.error || 
                          ERROR_MESSAGES[response.code] || 
                          'Volume deletion failed. Please try again.';
      notification.error(safeMessage);
      
      logError('Failed to delete volume', {
        volumeId,
        volumeName,
        response: response || 'No response',
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('delete_volume_failed', {
        volumeId,
        volumeName
      });
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Special case: 404 means already deleted
    if (err.status === 404 || err.code === 'VOLUME_NOT_FOUND' || err.code === 'RESOURCE_NOT_FOUND') {
      notification.success('Volume deleted successfully');
      debouncedFetchVolumes();
      logInfo('Volume already deleted, treating as success', {
        volumeId,
        volumeName,
        duration: `${duration.toFixed(2)}ms`
      });
      setDeleteModal({ open: false, volume: null });
      setIsDeleting(false);
      operationRefs.current.deleting.delete(volumeId);
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(volumeId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }
    
    // Position 1: Authentication error
    if (err.status === 401) {
      handleAuthError();
      setDeleteModal({ open: false, volume: null });
      setIsDeleting(false);
      operationRefs.current.deleting.delete(volumeId);
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(volumeId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }
    
    // Position 2: Network error
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      notification.error(err.error);
      logError('Network error in handleDeleteConfirm', { 
        code: err.code, 
        duration: `${duration.toFixed(2)}ms`,
        volumeId,
        volumeName
      }, err);
      setDeleteModal({ open: false, volume: null });
      setIsDeleting(false);
      operationRefs.current.deleting.delete(volumeId);
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(volumeId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      notification.error(err.error);
      setDeleteModal({ open: false, volume: null });
      setIsDeleting(false);
      operationRefs.current.deleting.delete(volumeId);
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(volumeId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }
    
    // Position 4: Mapped error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      notification.error(ERROR_MESSAGES[err.code]);
      logError('Delete volume error - mapped code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      setDeleteModal({ open: false, volume: null });
      setIsDeleting(false);
      operationRefs.current.deleting.delete(volumeId);
      setLoadingStates(prev => {
        const newDeleting = new Set(prev.deleting);
        newDeleting.delete(volumeId);
        return { ...prev, deleting: newDeleting };
      });
      return;
    }

    // Position 5: Fallback
    const safeMessage = err.error || getSafeErrorMessage(err);
    notification.error(safeMessage);

    logError('Error deleting volume', {
      volumeId,
      volumeName,
      errorName: err.name,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('delete_volume_error', {
      volumeId,
      volumeName
    });
  } finally {
    setDeleteModal({ open: false, volume: null });
    setIsDeleting(false);
    operationRefs.current.deleting.delete(volumeId);
    setLoadingStates(prev => {
      const newDeleting = new Set(prev.deleting);
      newDeleting.delete(volumeId);
      return { ...prev, deleting: newDeleting };
    });
  }
}, [deleteModal.volume, debouncedFetchVolumes, handleAuthError, notification]);

const handleAttachClick = useCallback((volume) => {
    if (isOSVolume(volume) && mapVolumeStatus(volume) === 'Attached') {
      notification.warning('OS/Bootable volumes already attached cannot be attached to other instances');
      return;
    }

    logUserAction('attach_volume_modal_opened', {
      volumeId: volume.id,
      volumeName: getVolumeDisplayName(volume),
      region: volume.region
    });
    
    logInfo('Opening attach volume modal', {
      volumeId: volume.id,
      volumeName: getVolumeDisplayName(volume),
      region: volume.region
    });
    
    setLoadingStates(prev => ({ ...prev, vms: true }));
    debouncedFetchVMs(volume.region);
    
    setAttachModal({
      open: true,
      volume: volume
    });
  }, [debouncedFetchVMs, notification]);
const handleAttachConfirm = useCallback(async (vmId, volumeId) => {
  if (operationRefs.current.attaching.has(volumeId)) {
    return;
  }
  
  const startTime = performance.now();
  
  operationRefs.current.attaching.add(volumeId);
  
  logUserAction('attach_volume_confirmed', {
    vmId,
    volumeId
  });
  
  logInfo('Starting volume attachment', {
    vmId,
    volumeId
  });
  
  setLoadingStates(prev => ({ 
    ...prev, 
    attaching: new Set([...prev.attaching, volumeId])
  }));
  
  try {
    const response = await api.attachVolumes(vmId, [volumeId]);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success) {
      setAttachedVolumeMap(prev => ({
        ...prev,
        [volumeId]: vmId
      }));
      
      setVolumes(prevVolumes => 
        prevVolumes.map(vol => 
          vol.id === volumeId 
            ? { ...vol, status: 'attached', instance_id: vmId }
            : vol
        )
      );
      
      setLockedVolumes(prev => new Set([...prev, volumeId]));
      
      setAttachModal({ open: false, volume: null });

      logPerformance('attach_volume', duration, {
        vmId,
        volumeId,
        success: true
      });

      logInfo('Volume attached successfully', {
        vmId,
        volumeId,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('attach_volume_success', {
        vmId,
        volumeId
      });
    } else {
      const errorCode = response.code;
      const safeMessage = response.error || ERROR_MESSAGES[errorCode] || getSafeErrorMessage(response);
      notification.error(safeMessage);
      
      setAttachModal({ open: false, volume: null });
      
      if (isStateMismatchError(errorCode)) {
        logInfo('State mismatch detected in attach, refreshing volumes', { 
          errorCode, 
          selectedRegion,
          volumeId,
          vmId
        });
        debouncedFetchVolumes();
      }
      
      logError('Failed to attach volume', {
        vmId,
        volumeId,
        errorCode,
        response: response || 'No response',
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('attach_volume_failed', {
        vmId,
        volumeId,
        errorCode
      });
      
      return response;
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401) {
      handleAuthError();
      operationRefs.current.attaching.delete(volumeId);
      setLoadingStates(prev => {
        const newAttaching = new Set(prev.attaching);
        newAttaching.delete(volumeId);
        return { ...prev, attaching: newAttaching };
      });
      return;
    }
    
    // Position 2: Network error
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      notification.error(err.error);
      logError('Network error in handleAttachConfirm', { 
        code: err.code, 
        duration: `${duration.toFixed(2)}ms`,
        volumeId,
        vmId
      }, err);
      operationRefs.current.attaching.delete(volumeId);
      setLoadingStates(prev => {
        const newAttaching = new Set(prev.attaching);
        newAttaching.delete(volumeId);
        return { ...prev, attaching: newAttaching };
      });
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      notification.error(err.error);
      operationRefs.current.attaching.delete(volumeId);
      setLoadingStates(prev => {
        const newAttaching = new Set(prev.attaching);
        newAttaching.delete(volumeId);
        return { ...prev, attaching: newAttaching };
      });
      return;
    }
    
    // Position 4: Mapped error code (including state mismatch)
    if (err.code && ERROR_MESSAGES[err.code]) {
      const safeMessage = ERROR_MESSAGES[err.code];
      notification.error(safeMessage);
      setAttachModal({ open: false, volume: null });
      
      if (isStateMismatchError(err.code)) {
        logInfo('State mismatch detected in attach catch, refreshing volumes', { 
          errorCode: err.code, 
          selectedRegion,
          volumeId,
          vmId
        });
        debouncedFetchVolumes();
      }
      
      logError('Attach volume error - mapped code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      operationRefs.current.attaching.delete(volumeId);
      setLoadingStates(prev => {
        const newAttaching = new Set(prev.attaching);
        newAttaching.delete(volumeId);
        return { ...prev, attaching: newAttaching };
      });
      return;
    }

    // Position 5: Fallback
    const safeMessage = err.error || getSafeErrorMessage(err);
    notification.error(safeMessage);

    logError('Error attaching volume', {
      vmId,
      volumeId,
      errorName: err.name,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('attach_volume_error', {
      vmId,
      volumeId
    });
  } finally {
    operationRefs.current.attaching.delete(volumeId);
    setLoadingStates(prev => {
      const newAttaching = new Set(prev.attaching);
      newAttaching.delete(volumeId);
      return { ...prev, attaching: newAttaching };
    });
  }
}, [handleAuthError, notification, debouncedFetchVolumes, selectedRegion]);

const handleDetachClick = useCallback((volume) => {
    if (volume.bootable === true || volume.is_os_volume === true || isOSVolume(volume)) {
      notification.warning('OS/Bootable volumes cannot be detached');
      return;
    }

    const vmStatus = volume.vm_status;
    
    logUserAction('detach_volume_modal_opened', {
      volumeId: volume.id,
      volumeName: getVolumeDisplayName(volume),
      vmStatus: vmStatus
    });
    
    logInfo('Opening detach volume modal', {
      volumeId: volume.id,
      volumeName: getVolumeDisplayName(volume),
      vmStatus: vmStatus,
      instanceId: volume.instance_id
    });
    
    setDetachConfirmModal({
      open: true,
      volume
    });
  }, [notification]);

  // Helper function to handle successful detachment
  const handleDetachSuccess = useCallback((volumeName, volumeId) => {
    logInfo('Handling successful detachment', {
      volumeName,
      volumeId
    });
    
    setVolumes(prevVolumes => 
      prevVolumes.map(vol => 
        vol.id === volumeId 
          ? { ...vol, status: 'available', instance_id: null, vm_status: null }
          : vol
      )
    );
    
    setLockedVolumes(prev => new Set([...prev, volumeId]));
    
    if (volumeId) {
      setAttachedVolumeMap(prev => {
        const newMap = {...prev};
        delete newMap[volumeId];
        return newMap;
      });
    }
  }, []);

  // Info Modal Handlers
  const handleInfoClick = useCallback((volume) => {
    logUserAction('volume_info_modal_opened', {
      volumeId: volume.id,
      volumeName: getVolumeDisplayName(volume),
      region: volume.region
    });
    
    setInfoModal({
      open: true,
      volume: volume
    });
  }, []);

const handleDetachConfirm = useCallback(async () => {
  const volume = detachConfirmModal.volume;
  
  if (!volume) {
    const safeMessage = getSafeErrorMessage({ code: 'INVALID_DATA' });
    notification.error(safeMessage);
    setDetachConfirmModal({ open: false, volume: null });
    
    logError('Detach volume failed - no volume provided');
    return;
  }
  
  const volumeId = volume.id;
  
  if (operationRefs.current.detaching.has(volumeId)) {
    return;
  }
  
  operationRefs.current.detaching.add(volumeId);
  
  const startTime = performance.now();
  const volumeName = getVolumeDisplayName(volume);
  const vmStatus = volume.vm_status;
  
  logUserAction('detach_volume_confirmed', {
    volumeId,
    volumeName,
    vmStatus: vmStatus
  });
  
  logInfo('Starting volume detachment', {
    volumeId,
    volumeName,
    vmStatus: vmStatus,
    instanceId: volume.instance_id
  });
  
  setLoadingStates(prev => ({ 
    ...prev, 
    detaching: new Set([...prev.detaching, volumeId])
  }));
  
  const vmId = volume.instance_id || attachedVolumeMap[volume.id];
  
  // Auto-find VM branch
  if (!vmId && mapVolumeStatus(volume) === 'Attached') {
    setDetachConfirmModal({ open: false, volume: null });
    
    try {
      const response = await api.getVMs();
      
      if (response.success) {
        const allVms = response.instances || [];
        const validStatuses = ['SHUTOFF', 'HIBERNATED', 'STOPPED'];
        
        const vm = allVms.find(vm => 
          vm.region === volume.region && 
          validStatuses.includes(vm.status) &&
          vm.server_uuid
        );
        
        if (vm) {
          if (vm.status !== 'SHUTOFF') {
            notification.error(`VM must be in SHUTOFF state for this operation (current state: ${vm.status})`);
            
            logError('VM not in SHUTOFF state for detachment', {
              vmId: vm.server_uuid,
              vmStatus: vm.status,
              volumeId,
              region: volume.region
            });
            
            operationRefs.current.detaching.delete(volumeId);
            setLoadingStates(prev => {
              const newDetaching = new Set(prev.detaching);
              newDetaching.delete(volumeId);
              return { ...prev, detaching: newDetaching };
            });
            return;
          }
          
          const detachResponse = await api.detachVolumes(vm.server_uuid, [volume.id]);
          const endTime = performance.now();
          const duration = endTime - startTime;
          
          if (detachResponse && detachResponse.success) {
            handleDetachSuccess(volumeName, volume.id);
            
            logPerformance('detach_volume_auto_vm', duration, {
              vmId: vm.server_uuid,
              volumeId,
              volumeName,
              success: true
            });

            logInfo('Volume detached successfully (auto-found VM)', {
              vmId: vm.server_uuid,
              volumeId,
              volumeName,
              duration: `${duration.toFixed(2)}ms`
            });

            logUserAction('detach_volume_success', {
              volumeId,
              volumeName,
              vmId: vm.server_uuid
            });
          } else {
            const errorCode = detachResponse.code;
            const safeMessage = detachResponse.error || ERROR_MESSAGES[errorCode] || getSafeErrorMessage(detachResponse);
            notification.error(safeMessage);
            
            if (isStateMismatchError(errorCode)) {
              logInfo('State mismatch detected in detach (auto-found VM), refreshing volumes', { 
                errorCode, 
                selectedRegion,
                volumeId,
                vmId: vm.server_uuid
              });
              debouncedFetchVolumes();
            }
            
            logError('Failed to detach volume (auto-found VM)', {
              vmId: vm.server_uuid,
              volumeId,
              volumeName,
              errorCode,
              response: detachResponse || 'No response',
              duration: `${duration.toFixed(2)}ms`
            });

            logUserAction('detach_volume_failed', {
              volumeId,
              volumeName,
              vmId: vm.server_uuid,
              errorCode
            });
          }
        } else {
          logError('Could not find suitable VM for detachment', {
            volumeId,
            volumeName,
            region: volume.region,
            availableVMs: allVms.length
          });
          
          notification.error(`Could not find a suitable VM in ${volume.region} region.`);
        }
      } else {
        logError('Failed to get VMs for detachment', {
          volumeId,
          volumeName,
          response: response || 'No response'
        });
        
        notification.error('Failed to get VMs');
      }
    } catch (err) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401) {
        handleAuthError();
        operationRefs.current.detaching.delete(volumeId);
        setLoadingStates(prev => {
          const newDetaching = new Set(prev.detaching);
          newDetaching.delete(volumeId);
          return { ...prev, detaching: newDetaching };
        });
        return;
      }
      
      // Position 2: Network error
      const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
      if (networkErrorCodes.includes(err.code)) {
        notification.error(err.error);
        logError('Network error in handleDetachConfirm (auto-found VM)', { 
          code: err.code, 
          duration: `${duration.toFixed(2)}ms`,
          volumeId,
          volumeName
        }, err);
        operationRefs.current.detaching.delete(volumeId);
        setLoadingStates(prev => {
          const newDetaching = new Set(prev.detaching);
          newDetaching.delete(volumeId);
          return { ...prev, detaching: newDetaching };
        });
        return;
      }
      
      // Position 3: Rate limit
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        notification.error(err.error);
        operationRefs.current.detaching.delete(volumeId);
        setLoadingStates(prev => {
          const newDetaching = new Set(prev.detaching);
          newDetaching.delete(volumeId);
          return { ...prev, detaching: newDetaching };
        });
        return;
      }
      
      // Position 4: Mapped error code
      if (err.code && ERROR_MESSAGES[err.code]) {
        notification.error(ERROR_MESSAGES[err.code]);
        logError('Detach volume error - mapped code (auto-found VM)', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        operationRefs.current.detaching.delete(volumeId);
        setLoadingStates(prev => {
          const newDetaching = new Set(prev.detaching);
          newDetaching.delete(volumeId);
          return { ...prev, detaching: newDetaching };
        });
        return;
      }

      // Position 5: Fallback
      const safeMessage = err.error || getSafeErrorMessage(err);
      notification.error(safeMessage);

      logError('Error during auto-detachment process', {
        volumeId,
        volumeName,
        errorName: err.name,
        duration: `${duration.toFixed(2)}ms`
      }, err);

      logUserAction('detach_volume_error', {
        volumeId,
        volumeName
      });
    } finally {
      operationRefs.current.detaching.delete(volumeId);
      setLoadingStates(prev => {
        const newDetaching = new Set(prev.detaching);
        newDetaching.delete(volumeId);
        return { ...prev, detaching: newDetaching };
      });
    }
    
    return;
  }
  
  // No VM ID found at all
  if (!vmId) {
    notification.error('Could not find a VM with this volume attached. Try refreshing the page or contact support.');
    setDetachConfirmModal({ open: false, volume: null });
    operationRefs.current.detaching.delete(volumeId);
    setLoadingStates(prev => {
      const newDetaching = new Set(prev.detaching);
      newDetaching.delete(volumeId);
      return { ...prev, detaching: newDetaching };
    });
    
    logError('Could not find VM ID for detachment', {
      volumeId,
      volumeName,
      attachedVolumeMapSize: Object.keys(attachedVolumeMap).length
    });
    
    return;
  }
  
  // Main detach branch (vmId exists)
  setDetachConfirmModal({ open: false, volume: null });
  
  try {
    const response = await api.detachVolumes(vmId, [volume.id]);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success) {
      handleDetachSuccess(volumeName, volume.id);
      
      logPerformance('detach_volume', duration, {
        vmId,
        volumeId,
        volumeName,
        success: true
      });

      logInfo('Volume detached successfully', {
        vmId,
        volumeId,
        volumeName,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('detach_volume_success', {
        volumeId,
        volumeName,
        vmId
      });
    } else {
      const errorCode = response.code;
      const safeMessage = response.error || ERROR_MESSAGES[errorCode] || getSafeErrorMessage(response);
      notification.error(safeMessage);
      
      if (isStateMismatchError(errorCode)) {
        logInfo('State mismatch detected in detach, refreshing volumes', { 
          errorCode, 
          selectedRegion,
          volumeId,
          vmId
        });
        debouncedFetchVolumes();
      }
      
      logError('Failed to detach volume', {
        vmId,
        volumeId,
        volumeName,
        errorCode,
        response: response || 'No response',
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('detach_volume_failed', {
        volumeId,
        volumeName,
        vmId,
        errorCode
      });
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401) {
      handleAuthError();
      operationRefs.current.detaching.delete(volumeId);
      setLoadingStates(prev => {
        const newDetaching = new Set(prev.detaching);
        newDetaching.delete(volumeId);
        return { ...prev, detaching: newDetaching };
      });
      return;
    }
    
    // Position 2: Network error
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      notification.error(err.error);
      logError('Network error in handleDetachConfirm (outer)', { 
        code: err.code, 
        duration: `${duration.toFixed(2)}ms`,
        volumeId,
        volumeName,
        vmId
      }, err);
      operationRefs.current.detaching.delete(volumeId);
      setLoadingStates(prev => {
        const newDetaching = new Set(prev.detaching);
        newDetaching.delete(volumeId);
        return { ...prev, detaching: newDetaching };
      });
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      notification.error(err.error);
      operationRefs.current.detaching.delete(volumeId);
      setLoadingStates(prev => {
        const newDetaching = new Set(prev.detaching);
        newDetaching.delete(volumeId);
        return { ...prev, detaching: newDetaching };
      });
      return;
    }
    
    // Position 4: Mapped error code (including state mismatch)
    if (err.code && ERROR_MESSAGES[err.code]) {
      const safeMessage = ERROR_MESSAGES[err.code];
      notification.error(safeMessage);
      
      if (isStateMismatchError(err.code)) {
        logInfo('State mismatch detected in detach catch, refreshing volumes', { 
          errorCode: err.code, 
          selectedRegion,
          volumeId,
          vmId
        });
        debouncedFetchVolumes();
      }
      
      logError('Detach volume error - mapped code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      operationRefs.current.detaching.delete(volumeId);
      setLoadingStates(prev => {
        const newDetaching = new Set(prev.detaching);
        newDetaching.delete(volumeId);
        return { ...prev, detaching: newDetaching };
      });
      return;
    }
    
    // Position 5: Fallback
    const safeMessage = err.error || getSafeErrorMessage(err);
    notification.error(safeMessage);
    
    logError('Error detaching volume', {
      vmId,
      volumeId,
      volumeName,
      errorName: err.name,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('detach_volume_error', {
      volumeId,
      volumeName,
      vmId
    });
  } finally {
    operationRefs.current.detaching.delete(volumeId);
    setLoadingStates(prev => {
      const newDetaching = new Set(prev.detaching);
      newDetaching.delete(volumeId);
      return { ...prev, detaching: newDetaching };
    });
  }
}, [detachConfirmModal.volume, attachedVolumeMap, handleAuthError, handleDetachSuccess, notification, debouncedFetchVolumes, selectedRegion]);


  const handleResizeClick = useCallback((volume) => {
    setInfoModal({ open: false, volume: null });
    setResizeModalOpen(true);
    setResizeSourceVolume(volume);
    
    logUserAction('resize_volume_modal_opened', {
      volumeId: volume.id,
      volumeName: getVolumeDisplayName(volume),
      currentSize: volume.size
    });
  }, []);

  // Clone Handlers
  const handleCloneClick = useCallback((volume) => {
    setInfoModal({ open: false, volume: null });
    setCloneModalOpen(true);
    setCloneSourceVolume(volume);
    
    logUserAction('clone_volume_modal_opened', {
      volumeId: volume.id,
      volumeName: getVolumeDisplayName(volume),
      region: volume.region
    });
  }, []);

  const handleRegionChange = useCallback((region) => {
    logUserAction('region_changed', {
      fromRegion: selectedRegion,
      toRegion: region
    });
    
    logInfo('Region changed', {
      fromRegion: selectedRegion,
      toRegion: region,
      currentVolumeCount: volumes.length
    });
    
    setSelectedRegion(region);
    setIsRegionDropdownOpen(false);
    setGroupPages({ 'In Progress': 1, Available: 1, Attached: 1 });
    setExpandedVolumeId(null);
    setSearchQuery('');
  }, [selectedRegion, volumes.length]);

  const toggleRegionDropdown = useCallback((e) => {
    e.stopPropagation();
    const newState = !isRegionDropdownOpen;
    
    logUserAction('region_dropdown_toggled', {
      opened: newState
    });
    
    setIsRegionDropdownOpen(newState);
  }, [isRegionDropdownOpen]);

  const handleRefresh = useCallback(async () => {
    if (retryAttempts.current >= maxRetries) {
      const errorMsg = 'Maximum refresh attempts reached. Please wait before trying again.';
      setError(errorMsg);
      
      logError('Maximum refresh attempts exceeded', {
        attempts: retryAttempts.current,
        maxRetries,
        selectedRegion
      });
      
      return;
    }
    
    retryAttempts.current++;
    
    setLockedVolumes(new Set());
    
    logUserAction('refresh_volumes', {
      attempt: retryAttempts.current,
      maxRetries,
      selectedRegion
    });
    
    logInfo('Refreshing volumes', {
      attempt: retryAttempts.current,
      maxRetries,
      currentVolumeCount: volumes.length,
      selectedRegion
    });
    
    try {
      await debouncedFetchVolumes();
      retryAttempts.current = 0;
      logInfo('Volume refresh completed successfully');
    } catch (err) {
      if (retryAttempts.current >= maxRetries) {
        const safeMessage = getSafeErrorMessage(err);
        setError(safeMessage);
        
        logError('Volume refresh failed after max retries', {
          attempts: retryAttempts.current,
          maxRetries,
          selectedRegion
        }, err);
      }
    }
  }, [debouncedFetchVolumes, volumes.length, selectedRegion]);

  // Handle opening create modal
  const handleOpenCreateModal = useCallback(async () => {
    if (!isProfileComplete) {
      navigate('/account');
      return;
    }
    if (!hasMinimumBalance) {
      return;
    }
    
    const capabilities = getRegionCapabilities(selectedRegion);
    if (capabilities.supportsOSImages && osImages.length === 0) {
      await debouncedFetchOsImages(selectedRegion);
    }
    
    setCreateModalOpen(true);
  }, [isProfileComplete, hasMinimumBalance, navigate, selectedRegion, osImages.length, debouncedFetchOsImages]);

  // Toggle expanded volume row
  const handleToggleExpand = useCallback((volumeId) => {
    setExpandedVolumeId(prev => prev === volumeId ? null : volumeId);
    
    logUserAction('toggle_volume_expand', {
      volumeId,
      expanded: expandedVolumeId !== volumeId
    });
  }, [expandedVolumeId]);

  // Render volume row (collapsed state)
  const renderVolumeRow = useCallback((volume) => {
    const volumeDisplayName = getVolumeDisplayName(volume);
    const volumeType = getVolumeTypeDisplay(volume);
    const volumeStatus = mapVolumeStatus(volume);
    const vmStatus = volume.vm_status ? getVMStatus({ status: volume.vm_status }, volume.region) : volume.vm_status;
    const canDelete = canDeleteVolume(volume);
    const canDetach = canDetachVolume(volume);
    const canAttach = canAttachVolume(volume);
    const isExpanded = expandedVolumeId === volume.id;
    const isLocked = lockedVolumes.has(volume.id);
    const supportsAdvancedOps = supportsAdvancedOperations(volume);

    return (
      <div key={volume.id}>
        {/* Collapsed Row */}
        <div 
          className="flex items-center px-4 py-2.5 border-b border-[#222] hover:bg-[#1a1a1a] transition-colors"
          style={{ borderLeft: '3px solid #6b7280' }}
        >
          <div 
            onClick={() => handleToggleExpand(volume.id)}
            className="flex items-center gap-4 flex-1 cursor-pointer min-w-0"
          >
            <span className="text-white text-sm font-medium min-w-[140px] truncate">
              {sanitizeText(volumeDisplayName)}
            </span>
            <span className="text-[#9ca3af] text-xs">
              {volume.size} GB
            </span>
            <span className="text-[#6b7280] text-xs">
              {sanitizeText(volumeType)}
            </span>
            
            {/* Status Badge */}
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              volumeStatus === 'Available' 
                ? 'bg-[rgba(202,217,238,0.1)] text-[#cad9ee] border border-[rgba(202,217,238,0.3)]'
                : 'bg-[rgba(202,217,238,0.15)] text-[#cad9ee] border border-[#cad9ee]'
            }`}>
              {volumeStatus}
            </span>
            
            {/* VM Name if attached */}
            {volume.instance_name && (
              <span className="text-[#60a5fa] text-xs">
                → {sanitizeText(volume.instance_name)}
              </span>
            )}
            
            {/* VM STATUS BADGE */}
            {volume.vm_status && volumeStatus === 'Attached' && (
              <span 
                className="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-semibold whitespace-nowrap"
                style={{
                  background: vmStatus === 'SHUTOFF' ? '#1c2b22' : '#1c1c1c',
                  color: vmStatus === 'SHUTOFF' ? '#a5c77f' : '#9ca3af',
                  border: vmStatus === 'SHUTOFF' ? '1px solid #a5c77f' : '1px solid #9ca3af',
                  boxShadow: vmStatus === 'SHUTOFF' ? '0 0 10px rgba(165, 199, 127, 0.2)' : 'none'
                }}
              >
                <span 
                  className="w-1.5 h-1.5 rounded-full mr-2"
                  style={{
                    background: vmStatus === 'SHUTOFF' ? '#a5c77f' : '#9ca3af',
                    animation: vmStatus === 'SHUTOFF' ? 'pulse 2s infinite' : 'none'
                  }}
                ></span>
                VM Status: {vmStatus}
              </span>
            )}
            
            {/* Bootable badge */}
            {(volume.bootable || volume.is_os_volume) && (
              <span className="text-[10px] text-[#a5c77f] bg-[rgba(165,199,127,0.1)] px-1.5 py-0.5 rounded">
                Boot
              </span>
            )}
          </div>
          
          {/* Action Button */}
          {volumeStatus === 'Attached' ? (
            <button
              onClick={() => handleDetachClick(volume)}
              disabled={loadingStates.detaching.has(volume.id) || !canDetach || isLocked}
              className={`px-3 py-1.5 rounded text-xs flex items-center gap-1.5 transition-colors ${
                (!canDetach || isLocked)
                  ? 'bg-[#333] text-[#666] cursor-not-allowed'
                  : 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]'
              }`}
              title={
                isOSVolume(volume) 
                  ? 'OS/Bootable volumes cannot be detached'
                  : vmStatus && vmStatus !== 'SHUTOFF' 
                    ? `VM must be SHUTOFF to detach volumes (current status: ${vmStatus})` 
                    : 'Detach Volume'
              }
            >
              {loadingStates.detaching.has(volume.id) ? (
                <>
                  <div className="animate-spin h-3 w-3 border-2 border-t-black border-black border-opacity-20 rounded-full"></div>
                  <span>Detaching...</span>
                </>
              ) : (
                <>
                  <FaUnlink size={10} />
                  <span>Detach</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={() => handleAttachClick(volume)}
              disabled={loadingStates.attaching.has(volume.id) || !canAttach || isLocked}
              className={`px-3 py-1.5 rounded text-xs flex items-center gap-1.5 transition-colors ${
                (!canAttach || isLocked)
                  ? 'bg-[#333] text-[#666] cursor-not-allowed'
                  : 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]'
              }`}
              title="Attach Volume"
            >
              {loadingStates.attaching.has(volume.id) ? (
                <>
                  <div className="animate-spin h-3 w-3 border-2 border-t-black border-black border-opacity-20 rounded-full"></div>
                  <span>Attaching...</span>
                </>
              ) : (
                <>
                  <FaLink size={10} />
                  <span>Attach</span>
                </>
              )}
            </button>
          )}
          
          {/* Expand chevron */}
          <div 
            onClick={() => handleToggleExpand(volume.id)}
            className="ml-3 cursor-pointer p-1"
          >
            <FaChevronRight 
              size={10} 
              className={`text-[#6b7280] transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
            />
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div className="px-4 py-4 bg-[#0d0d0d] border-b border-[#222]">
            <div className="grid grid-cols-[1fr_auto] gap-4">
              {/* Details Grid */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <span className="text-[10px] text-[#6b7280] uppercase block mb-1">Volume ID</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-white">{sanitizeText(volume.id)}</span>
                    <button 
                      onClick={() => copyToClipboard(volume.id)}
                      className="p-0.5 bg-transparent border-none cursor-pointer"
                    >
                      <FaCopy size={10} className={copiedId === volume.id ? 'text-[#a5c77f]' : 'text-[#6b7280]'} />
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-[10px] text-[#6b7280] uppercase block mb-1">Region</span>
                  <span className="text-xs text-white">{volume.region}</span>
                </div>
                <div>
                  <span className="text-[10px] text-[#6b7280] uppercase block mb-1">Created</span>
                  <span className="text-xs text-white">
                    {volume.created_at ? new Date(volume.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-[#6b7280] uppercase block mb-1">Bootable</span>
                  <span className="text-xs text-white">{(volume.bootable || volume.is_os_volume) ? 'Yes' : 'No'}</span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-1.5">
                {supportsAdvancedOps && (
                  <>
                    <button
                      onClick={() => handleResizeClick(volume)}
                      disabled={loadingStates.resizing.has(volume.id) || !(volumeStatus === 'Attached' || volumeStatus === 'Available')}
                      className={`px-3.5 py-2 rounded-md text-xs flex items-center gap-2 justify-center min-w-[90px] border-none transition-colors ${
                        (loadingStates.resizing.has(volume.id) || !(volumeStatus === 'Attached' || volumeStatus === 'Available'))
                          ? 'bg-[#333] text-[#666] cursor-not-allowed'
                          : 'bg-[#333] text-white hover:bg-[#444] cursor-pointer'
                      }`}
                    >
                      <FaExpand size={10} />
                      <span>Resize</span>
                    </button>
                    <button
                      onClick={() => handleCloneClick(volume)}
                      disabled={loadingStates.cloning.has(volume.id) || !(volumeStatus === 'Available' || (volumeStatus === 'Attached' && vmStatus === 'SHUTOFF'))}
                      className={`px-3.5 py-2 rounded-md text-xs flex items-center gap-2 justify-center min-w-[90px] border-none transition-colors ${
                        (loadingStates.cloning.has(volume.id) || !(volumeStatus === 'Available' || (volumeStatus === 'Attached' && vmStatus === 'SHUTOFF')))
                          ? 'bg-[#333] text-[#666] cursor-not-allowed'
                          : 'bg-[#333] text-white hover:bg-[#444] cursor-pointer'
                      }`}
                    >
                      <FaCopy size={10} />
                      <span>Clone</span>
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleDeleteClick(volume)}
                  disabled={loadingStates.deleting.has(volume.id) || !canDelete || isLocked}
                  className={`px-3.5 py-2 rounded-md text-xs flex items-center gap-2 justify-center min-w-[90px] transition-colors ${
                    (!canDelete || isLocked)
                      ? 'bg-[#1a1a1a] text-[#555] border border-[#333] cursor-not-allowed'
                      : 'bg-[rgba(238,85,85,0.1)] text-[#ff7777] border border-[#ff7777] hover:bg-[rgba(238,85,85,0.2)] cursor-pointer'
                  }`}
                >
                  <FaTrash size={10} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [
    expandedVolumeId, 
    lockedVolumes, 
    loadingStates, 
    copiedId, 
    handleToggleExpand, 
    handleAttachClick, 
    handleDetachClick, 
    handleDeleteClick, 
    handleResizeClick, 
    handleCloneClick,
    copyToClipboard
  ]);

  // Render volume group
  const renderVolumeGroup = useCallback((groupName, groupVolumes) => {
    if (groupVolumes.length === 0) return null;
    
    const isCollapsed = collapsedGroups[groupName];
    const currentPage = groupPages[groupName] || 1;
    const totalPages = getGroupTotalPages(groupName);
    const paginatedVolumes = getPaginatedGroupVolumes(groupName);
    const totalSize = groupVolumes.reduce((sum, v) => sum + (v.size || 0), 0);
    
    return (
      <div key={groupName}>
        {/* Group Header */}
        <div 
          onClick={() => toggleGroup(groupName)}
          className="flex justify-between items-center px-4 py-3 bg-[#1a1a1a] border-b border-[#333] cursor-pointer hover:bg-[#222] transition-colors"
        >
          <div className="flex items-center gap-2.5 text-[#e0eacc] text-sm font-semibold">
            <FaChevronRight 
              size={10} 
              className={`transition-transform duration-200 ${isCollapsed ? '' : 'rotate-90'}`}
            />
            {groupName}
            <span className="bg-[#333] px-2 py-0.5 rounded-full text-xs text-[#9ca3af] font-normal">
              {groupVolumes.length}
            </span>
            {groupVolumes.length > GROUP_ITEMS_PER_PAGE && (
              <span className="bg-[rgba(224,234,204,0.1)] px-2 py-0.5 rounded-full text-[10px] text-[#e0eacc]">
                {totalPages} pages
              </span>
            )}
          </div>
          <div className="text-xs text-[#6b7280]">
            {totalSize} GB total
          </div>
        </div>
        
        {/* Group Content */}
        {!isCollapsed && (
          <>
            {paginatedVolumes.map(renderVolumeRow)}
            
            {/* Group Pagination */}
            <GroupPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => handleGroupPageChange(groupName, page)}
              totalItems={groupVolumes.length}
            />
          </>
        )}
      </div>
    );
  }, [
    collapsedGroups, 
    groupPages, 
    getGroupTotalPages, 
    getPaginatedGroupVolumes, 
    toggleGroup, 
    renderVolumeRow,
    handleGroupPageChange
  ]);

  // MOBILE LAYOUT
  if (isMobile || isTablet) {
    return (
      <div className="flex bg-black min-h-screen text-white">
        <div className={`flex-1 ${isMobile ? 'p-4' : isTablet ? 'p-6' : 'p-8'}`}>
          <div className="max-w-6xl mx-auto">
            {/* Header Section */}
            <div className={`mb-8 bg-[#121212] border border-[#333333] rounded-xl shadow-xl ${isMobile ? 'p-6' : 'p-8'}`}>
              <div className={isMobile ? 'space-y-6' : 'flex items-center justify-between'}>
                <div className="flex items-center">
                  <div className={`bg-[#222222] border border-[#333333] rounded-full shadow-sm mr-6 ${isMobile ? 'p-3' : 'p-4'}`}>
<HardDrive size={isMobile ? 16 : 20} strokeWidth={1.5} className="text-[#e0eacc]" />
                  </div>
                  <div>
                    <h1 className={`font-medium text-white ${isMobile ? 'text-2xl' : 'text-3xl'}`}>Storage Volumes</h1>
                    <p className={`text-gray-400 mt-2 ${isMobile ? 'text-sm' : 'text-base'}`}>
                      Manage persistent storage for your virtual machines
                    </p>
                  </div>
                </div>
                
                {/* Actions - Tablet Only */}
                {!isMobile && (
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleOpenCreateModal}
                      className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                        !isProfileComplete || !hasMinimumBalance
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                          : 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]'
                      }`}
                      disabled={loadingStates.creating || !isProfileComplete || !hasMinimumBalance}
                      title={getCreateButtonTooltip}
                    >
                      <FaPlus className="mr-2" /> Create Volume
                    </button>
                    
                    <div className="relative" ref={regionDropdownRef}>
                      <div 
                        className="w-36 px-3 py-3 bg-[#1d1d1d] border border-[#333333] hover:border-[#cad9e3] rounded-md text-white text-sm flex justify-between items-center cursor-pointer transition-colors duration-150"
                        onClick={toggleRegionDropdown}
                      >
                        <span>{selectedRegion}</span>
                        <div className="flex space-x-0.5">
                          {[0, 1, 2].map((i) => (
                            <div 
                              key={i}
                              className={`w-1 h-1 bg-[#cad9e3] rounded-full transition-all duration-200 ${
                                isRegionDropdownOpen ? 'animate-bounce' : ''
                              }`}
                              style={{ animationDelay: `${i * 100}ms` }}
                            />
                          ))}
                        </div>
                      </div>
                      
                      {isRegionDropdownOpen && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-[#1d1d1d] border border-[#333333] rounded-md shadow-lg overflow-hidden">
                          <div className="py-1">
                            {regions.map((region) => (
                              <div 
                                key={region} 
                                className={`mx-2 px-2 py-1.5 cursor-pointer transition-all duration-200 border border-transparent rounded hover:border-[#cad9e3] ${
                                  region === selectedRegion 
                                    ? 'bg-[#252525] text-white border-[#cad9e3]' 
                                    : 'text-gray-200 hover:bg-[#252525] hover:text-white'
                                }`}
                                onClick={() => handleRegionChange(region)}
                              >
                                {region}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={handleRefresh}
                      className="flex items-center px-3 py-2 bg-[#333333] hover:bg-[#444444] rounded-md text-sm transition-colors relative overflow-hidden"
                      disabled={loadingStates.volumes}
                    >
                      <div 
                        className={`absolute top-0 left-0 h-full bg-gradient-to-r from-[#cad9e3] to-[#b8c7d1] transition-all duration-300 rounded-md ${
                          loadingStates.volumes ? 'w-full' : 'w-0'
                        }`}
                      ></div>
                      
                      <div className="relative z-10 flex items-center">
                        <svg 
                          className={`mr-2 w-4 h-4 ${loadingStates.volumes ? 'animate-spin' : ''}`} 
                          xmlns="http://www.w3.org/2000/svg" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                          />
                        </svg>
                        <span className={loadingStates.volumes ? 'text-black' : 'text-white'}>
                          Refresh
                        </span>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Actions */}
              {isMobile && (
                <div className="mt-6 space-y-3">
                  <div className="flex space-x-3">
                    <button
                      onClick={handleOpenCreateModal}
                      className={`flex-1 flex items-center justify-center px-4 py-3 rounded-md font-medium transition-colors ${
                        !isProfileComplete || !hasMinimumBalance
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                          : 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]'
                      }`}
                      disabled={loadingStates.creating || !isProfileComplete || !hasMinimumBalance}
                      title={getCreateButtonTooltip}
                    >
                      <FaPlus className="mr-2" size={14} /> Create Volume
                    </button>
                    
                    <button
                      onClick={handleRefresh}
                      className="flex items-center justify-center px-4 py-3 bg-[#333333] hover:bg-[#444444] rounded-lg text-sm transition-colors relative overflow-hidden"
                      disabled={loadingStates.volumes}
                    >
                      <div 
                        className={`absolute top-0 left-0 h-full bg-gradient-to-r from-[#cad9e3] to-[#b8c7d1] transition-all duration-300 rounded-lg ${
                          loadingStates.volumes ? 'w-full' : 'w-0'
                        }`}
                      ></div>
                      
                      <div className="relative z-10 flex items-center">
                        <svg 
                          className={`mr-2 w-4 h-4 ${loadingStates.volumes ? 'animate-spin' : ''}`} 
                          xmlns="http://www.w3.org/2000/svg" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                          />
                        </svg>
                        <span className={loadingStates.volumes ? 'text-black' : 'text-white'}>
                          Refresh
                        </span>
                      </div>
                    </button>
                  </div>
                  
                  <div className="relative" ref={regionDropdownRef}>
                    <div 
                      className="w-full px-4 py-3 bg-[#1d1d1d] border border-[#333333] hover:border-[#cad9e3] rounded-md text-white text-sm flex justify-between items-center cursor-pointer transition-colors duration-150"
                      onClick={toggleRegionDropdown}
                    >
                      <span>Region: {selectedRegion}</span>
                      <div className="flex space-x-0.5">
                        {[0, 1, 2].map((i) => (
                          <div 
                            key={i}
                            className={`w-1 h-1 bg-[#cad9e3] rounded-full transition-all duration-200 ${
                              isRegionDropdownOpen ? 'animate-bounce' : ''
                            }`}
                            style={{ animationDelay: `${i * 100}ms` }}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {isRegionDropdownOpen && (
                      <div className="absolute z-50 left-0 right-0 mt-1 bg-[#1d1d1d] border border-[#333333] rounded-md shadow-lg overflow-hidden">
                        <div className="py-1">
                          {regions.map((region) => (
                            <div 
                              key={region} 
                              className={`mx-2 px-3 py-2 cursor-pointer transition-all duration-200 border border-transparent rounded hover:border-[#cad9e3] ${
                                region === selectedRegion 
                                  ? 'bg-[#252525] text-white border-[#cad9e3]' 
                                  : 'text-gray-200 hover:bg-[#252525] hover:text-white'
                              }`}
                              onClick={() => handleRegionChange(region)}
                            >
                              {region}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6 bg-red-900 bg-opacity-20 border border-red-800 rounded-md p-4 flex items-center justify-between">
                <p className="text-red-400 text-sm">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    handleRefresh();
                  }}
                  className="ml-4 px-3 py-1 bg-red-700 hover:bg-red-600 text-white text-xs rounded transition-colors"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Search and Sort */}
            <div className="flex gap-3 items-center mb-4">
              <div className="flex items-center bg-[#1a1a1a] border border-[#333] rounded-md px-3 py-2 flex-1 max-w-[300px]">
                <FaSearch size={14} className="text-[#6b7280]" />
                <input
                  type="text"
                  placeholder="Search volumes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  maxLength={30}
                  className="bg-transparent border-none outline-none text-white ml-2 text-sm w-full"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="bg-transparent border-none cursor-pointer p-0"
                  >
                    <FaTimes size={12} className="text-[#6b7280]" />
                  </button>
                )}
              </div>
              
              <div className="relative">
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-[#1a1a1a] border border-[#333] rounded-md text-[#9ca3af] text-xs cursor-pointer"
                >
                  <FaSort size={12} />
                  {sortOptions.find(s => s.id === sortBy)?.label}
                </button>
                {sortDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-[#1a1a1a] border border-[#333] rounded-md overflow-hidden z-50 min-w-[100px]">
                    {sortOptions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setSortBy(s.id); setSortDropdownOpen(false); }}
                        className={`px-3 py-2 text-xs cursor-pointer ${
                          sortBy === s.id 
                            ? 'text-[#e0eacc] bg-[rgba(224,234,204,0.1)]' 
                            : 'text-[#9ca3af] hover:bg-[#252525]'
                        }`}
                      >
                        {s.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="ml-auto text-[#6b7280] text-xs">
                {filteredVolumes.length} volume{filteredVolumes.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Loading State */}
            {loadingStates.volumes ? (
              <div className="bg-[#1a1a1a] border border-[#333333] rounded-xl shadow-xl flex items-center justify-center p-8">
                <div className="w-8 h-8 border-4 border-t-[#e0eacc] border-[#e0eacc] border-opacity-20 rounded-full animate-spin"></div>
                <p className="text-gray-400 ml-4 text-sm">Loading volumes...</p>
              </div>
            ) : filteredVolumes.length === 0 ? (
              /* Empty State */
              <div className="bg-[#121212] rounded-xl border border-[#333333] p-6">
                <div className="flex flex-col items-center justify-center py-[70px]">
                  <div className="mb-6">
                    <FaDatabase className="text-[#e0eacc]" size={40} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">No Volumes Found</h3>
                  <p className="text-gray-400 text-center max-w-md text-sm">
                    {searchQuery 
                      ? `No volumes matching "${searchQuery}" in ${selectedRegion}`
                      : `You haven't created any volumes in ${selectedRegion} yet.`
                    }
                  </p>
                </div>
              </div>
            ) : (
              /* Volume Groups List */
              <div className="bg-[#121212] rounded-xl border border-[#333] overflow-hidden">
                {renderVolumeGroup('In Progress', groupedVolumes['In Progress'])}
                {renderVolumeGroup('Available', groupedVolumes.Available)}
                {renderVolumeGroup('Attached', groupedVolumes.Attached)}
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <CreateVolumeModal
          mode="create"
          isOpen={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            logUserAction('create_volume_modal_closed');
          }}
          onSave={handleCreateVolume}
          regions={regions}
          images={osImages}
          existingVolumeNames={getFilteredVolumeNames(volumes, selectedRegion)}
          initialName={nextVolumeName}
          selectedRegion={selectedRegion}
          volumePricing={volumePricing}
        />

        <CreateVolumeModal
          mode="clone"
          isOpen={cloneModalOpen}
          onClose={() => {
            setCloneModalOpen(false);
            setCloneSourceVolume(null);
            logUserAction('clone_volume_modal_closed');
          }}
          onClone={handleCloneVolume}
          sourceVolume={cloneSourceVolume}
          regions={regions}
          images={osImages}
          existingVolumeNames={getFilteredVolumeNames(volumes, selectedRegion)}
          selectedRegion={selectedRegion}
          volumePricing={volumePricing}
        />

        <CreateVolumeModal
          mode="resize"
          isOpen={resizeModalOpen}
          onClose={() => {
            setResizeModalOpen(false);
            setResizeSourceVolume(null);
            logUserAction('resize_volume_modal_closed');
          }}
          onResize={handleResizeVolume}
          sourceVolume={resizeSourceVolume}
          regions={regions}
          images={osImages}
          existingVolumeNames={getFilteredVolumeNames(volumes, selectedRegion)}
          selectedRegion={selectedRegion}
          volumePricing={volumePricing}
        />

        <VolumeInfoModal
          isOpen={infoModal.open}
          onClose={() => setInfoModal({ open: false, volume: null })}
          volume={infoModal.volume}
          onResize={handleResizeClick}
          onClone={handleCloneClick}
          isResizing={loadingStates.resizing && loadingStates.resizing.has(infoModal.volume?.id)}
          isCloning={loadingStates.cloning && loadingStates.cloning.size > 0}
        />

       {/* Delete Confirmation Modal */}
{deleteModal.open && deleteModal.volume && (
  <div style={{
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px'
  }}>
    <div style={{
      backgroundColor: '#121212',
      border: '1px solid #333333',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '420px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        borderBottom: '1px solid #2a2a2a'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff7777">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
          </svg>
          <h3 style={{
            fontWeight: '600',
            color: '#fff',
            margin: 0,
            fontSize: '16px'
          }}>
            Delete Volume
          </h3>
        </div>
        <button
          onClick={() => {
            setDeleteModal({ open: false, volume: null });
            logUserAction('delete_volume_modal_closed');
          }}
          disabled={isDeleting}
          style={{
            padding: '8px',
            borderRadius: '6px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#6b7280',
            cursor: isDeleting ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div style={{ padding: '20px' }}>
        <p style={{
          color: '#9ca3af',
          marginBottom: '16px',
          marginTop: 0,
          fontSize: '14px',
          lineHeight: '1.6'
        }}>
          Are you sure you want to delete this volume?
        </p>
        
        {/* Volume Name Box */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #2a2a2a',
          borderRadius: '8px',
          padding: '12px 14px',
          marginBottom: '16px'
        }}>
          <span style={{
            color: '#6b7280',
            fontSize: '11px',
            display: 'block',
            marginBottom: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Volume Name
          </span>
          <span style={{
            color: '#e0eacc',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {sanitizeText(getVolumeDisplayName(deleteModal.volume))}
          </span>
        </div>
        
        {/* Warning Box */}
        <div style={{
          backgroundColor: 'rgba(255,119,119,0.05)',
          border: '1px solid rgba(255,119,119,0.2)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff8080" style={{ flexShrink: 0 }}>
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          <span style={{ color: '#ff8080', fontSize: '12px' }}>
            This action cannot be undone.
          </span>
        </div>
        
        {/* Delete Button */}
        <button
          onClick={handleDeleteConfirm}
          disabled={isDeleting}
          style={{
            width: '100%',
            backgroundColor: isDeleting ? '#4a3030' : 'rgba(255,100,100,0.15)',
            border: '1px solid rgba(255,119,119,0.4)',
            color: '#ff7777',
            borderRadius: '8px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 20px',
            fontSize: '14px',
            cursor: isDeleting ? 'not-allowed' : 'pointer',
            gap: '8px'
          }}
        >
          {isDeleting ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid transparent',
                borderTopColor: '#ff7777',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Deleting...
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
              Delete Volume
            </>
          )}
        </button>
      </div>
    </div>
  </div>
)}

        <VolumeAttachModal
          isOpen={attachModal.open}
          onClose={() => {
            setAttachModal({ open: false, volume: null });
            logUserAction('attach_volume_modal_closed');
          }}
          onAttach={handleAttachConfirm}
          volume={attachModal.volume || {}}
          vms={vms}
          isAttaching={loadingStates.attaching.size > 0}
          isLoadingVms={loadingStates.vms}
        />

        {/* Detach Confirmation Modal */}
        {detachConfirmModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a1a] rounded-md border border-[#333333] shadow-md w-full max-w-sm p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-white text-lg">Detach Volume</h3>
                <button
                  onClick={() => {
                    setDetachConfirmModal({ open: false, volume: null });
                    logUserAction('detach_volume_modal_closed');
                  }}
                  className="text-gray-400 hover:text-white transition-colors bg-transparent border-none cursor-pointer"
                >
                  <FaTimes />
                </button>
              </div>
              
              {detachConfirmModal.volume && detachConfirmModal.volume.vm_status && 
               detachConfirmModal.volume.vm_status !== 'SHUTOFF' ? (
                <div className="bg-[#331111] p-4 rounded-md mb-4">
                  <p className="text-red-300 flex items-start text-sm">
                    <FaExclamationTriangle className="mt-1 mr-2 flex-shrink-0" />
                    <span>
                      <strong>Cannot detach:</strong> VM must be <strong>SHUTOFF</strong>. Current: <strong>{sanitizeText(detachConfirmModal.volume.vm_status)}</strong>
                    </span>
                  </p>
                </div>
              ) : (
                <div className="bg-[#142218] p-4 rounded-md mb-4">
                  <p className="text-[#e0eacc] flex items-start text-sm">
                    <FaExclamationTriangle className="mt-1 mr-2 flex-shrink-0" />
                    <span>
                      Are you sure you want to detach volume <strong>{sanitizeText(getVolumeDisplayName(detachConfirmModal.volume))}</strong>?
                    </span>
                  </p>
                </div>
              )}
              
              <div className="flex gap-3 mt-6 flex-col">
                <button
                  onClick={() => {
                    setDetachConfirmModal({ open: false, volume: null });
                    logUserAction('detach_volume_cancelled');
                  }}
                  className="bg-[#333333] hover:bg-[#444444] text-white rounded-md transition-colors py-3 px-4 text-sm w-full"
                >
                  Cancel
                </button>
                
                {!(detachConfirmModal.volume && detachConfirmModal.volume.vm_status && 
                  detachConfirmModal.volume.vm_status !== 'SHUTOFF') && (
                  <button
                    onClick={handleDetachConfirm}
                    className="bg-[#e0eacc] hover:bg-[#c8d9a9] text-black rounded-md font-medium flex items-center justify-center py-3 px-4 text-sm w-full min-w-[80px]"
                  >
                    Detach
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // DESKTOP LAYOUT
  return (
    <div className="flex bg-black min-h-screen text-white">
      <Sidebar 
        activePage="volumes" 
        isMobile={isMobile}
        isTablet={isTablet}
        isSidebarOpen={false}
        toggleSidebar={() => {}}
        authenticated={true}
        onAuthAction={() => {}}
      />
      
      <div className="flex-1">
        <div className="max-w-none w-[118%] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex justify-between items-center py-6 border-b border-[#333333]">
            <div className="flex items-center">
              <div className="mr-4">
<HardDrive size={20} strokeWidth={1.5} className="text-[#e0eacc]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Storage Volumes</h1>
                <p className="text-gray-400 text-sm">Manage persistent storage for your virtual machines</p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleOpenCreateModal}
                className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                  !isProfileComplete || !hasMinimumBalance
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9]'
                }`}
                disabled={loadingStates.creating || !isProfileComplete || !hasMinimumBalance}
                title={getCreateButtonTooltip}
              >
                <FaPlus className="mr-2" /> Create Volume
              </button>
              
              <div className="relative" ref={regionDropdownRef}>
                <div 
                  className="w-36 px-3 py-3 bg-[#1d1d1d] border border-[#333333] hover:border-[#cad9e3] rounded-md text-white text-sm flex justify-between items-center cursor-pointer transition-colors duration-150"
                  onClick={toggleRegionDropdown}
                >
                  <span>{selectedRegion}</span>
                  <div className="flex space-x-0.5">
                    {[0, 1, 2].map((i) => (
                      <div 
                        key={i}
                        className={`w-1 h-1 bg-[#cad9e3] rounded-full transition-all duration-200 ${
                          isRegionDropdownOpen ? 'animate-bounce' : ''
                        }`}
                        style={{ animationDelay: `${i * 100}ms` }}
                      />
                    ))}
                  </div>
                </div>
                
                {isRegionDropdownOpen && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-[#1d1d1d] border border-[#333333] rounded-md shadow-lg overflow-hidden">
                    <div className="py-1">
                      {regions.map((region) => (
                        <div 
                          key={region} 
                          className={`mx-2 px-2 py-1.5 cursor-pointer transition-all duration-200 border border-transparent rounded hover:border-[#cad9e3] ${
                            region === selectedRegion 
                              ? 'bg-[#252525] text-white border-[#cad9e3]' 
                              : 'text-gray-200 hover:bg-[#252525] hover:text-white'
                          }`}
                          onClick={() => handleRegionChange(region)}
                        >
                          {region}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={handleRefresh}
                className="flex items-center px-3 py-2 bg-[#333333] hover:bg-[#444444] rounded-md text-sm transition-colors relative overflow-hidden"
                disabled={loadingStates.volumes}
              >
                <div 
                  className={`absolute top-0 left-0 h-full bg-gradient-to-r from-[#cad9e3] to-[#b8c7d1] transition-all duration-300 rounded-md ${
                    loadingStates.volumes ? 'w-full' : 'w-0'
                  }`}
                ></div>
                
                <div className="relative z-10 flex items-center">
                  <svg 
                    className={`mr-2 w-4 h-4 ${loadingStates.volumes ? 'animate-spin' : ''}`} 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth="2" 
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                    />
                  </svg>
                  <span className={loadingStates.volumes ? 'text-black' : 'text-white'}>
                    Refresh
                  </span>
                </div>
              </button>
            </div>
          </div>
          
          <div className="py-6" style={{ minHeight: '120vh' }}>
            {/* Error Display */}
            {error && (
              <div className="mb-6 bg-red-900 bg-opacity-20 border border-red-800 rounded-md p-4 flex items-center justify-between">
                <p className="text-red-400 text-sm">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    handleRefresh();
                  }}
                  className="ml-4 px-3 py-1 bg-red-700 hover:bg-red-600 text-white text-xs rounded transition-colors flex-shrink-0"
                >
                  Retry
                </button>
              </div>
            )}

            {statusMessage && (
              <div className="bg-[#113322] border border-green-700 p-4 rounded-md mb-6 flex items-center">
                <div className="bg-[#e0eacc] p-1 rounded-full mr-3">
                  <FaCheck className="text-black" size={12} />
                </div>
                <p className="text-green-300">{statusMessage}</p>
              </div>
            )}

            {/* Search and Sort Bar */}
            <div className="flex gap-3 items-center mb-4">
              <div className="flex items-center bg-[#1a1a1a] border border-[#333] rounded-md px-3 py-2 flex-1 max-w-[300px]">
                <FaSearch size={14} className="text-[#6b7280]" />
                <input
                  type="text"
                  placeholder="Search volumes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  maxLength={30}
                  className="bg-transparent border-none outline-none text-white ml-2 text-sm w-full"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="bg-transparent border-none cursor-pointer p-0"
                  >
                    <FaTimes size={12} className="text-[#6b7280]" />
                  </button>
                )}
              </div>
              
              <div className="relative">
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-[#1a1a1a] border border-[#333] rounded-md text-[#9ca3af] text-xs cursor-pointer hover:border-[#555]"
                >
                  <FaSort size={12} />
                  {sortOptions.find(s => s.id === sortBy)?.label}
                </button>
                {sortDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-[#1a1a1a] border border-[#333] rounded-md overflow-hidden z-50 min-w-[100px]">
                    {sortOptions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setSortBy(s.id); setSortDropdownOpen(false); }}
                        className={`px-3 py-2 text-xs cursor-pointer ${
                          sortBy === s.id 
                            ? 'text-[#e0eacc] bg-[rgba(224,234,204,0.1)]' 
                            : 'text-[#9ca3af] hover:bg-[#252525]'
                        }`}
                      >
                        {s.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="ml-auto text-[#6b7280] text-xs">
                {filteredVolumes.length} volume{filteredVolumes.length !== 1 ? 's' : ''}
              </div>
            </div>
            
            {/* Loading State */}
            {loadingStates.volumes ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-12 h-12 border-4 border-t-[#e0eacc] border-[#e0eacc] border-opacity-20 rounded-full animate-spin"></div>
                <p className="text-gray-400 ml-4">Loading volumes...</p>
              </div>
            ) : filteredVolumes.length === 0 ? (
              /* Empty State */
              <div className="bg-[#121212] rounded-[10px] p-8 border border-[#333333] shadow-[0_6px_24px_rgba(0,0,0,0.4)]">
                <div className="flex flex-col items-center justify-center py-[90px]">
                  <div className="mb-6">
                    <FaDatabase className="text-[#e0eacc]" size={48} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">No Volumes Found</h3>
                  <p className="text-gray-400 text-center max-w-md">
                    {searchQuery 
                      ? `No volumes matching "${searchQuery}" in ${selectedRegion}`
                      : `You haven't created any volumes in the ${selectedRegion} region yet.`
                    }
                  </p>
                </div>
              </div>
            ) : (
              /* Volume Groups List */
              <div className="bg-[#121212] rounded-[10px] border border-[#333] overflow-hidden">
                {renderVolumeGroup('In Progress', groupedVolumes['In Progress'])}
                {renderVolumeGroup('Available', groupedVolumes.Available)}
                {renderVolumeGroup('Attached', groupedVolumes.Attached)}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <CreateVolumeModal
        mode="create"
        isOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          logUserAction('create_volume_modal_closed');
        }}
        onSave={handleCreateVolume}
        regions={regions}
        images={osImages}
        existingVolumeNames={getFilteredVolumeNames(volumes, selectedRegion)}
        initialName={nextVolumeName}
        selectedRegion={selectedRegion}
        volumePricing={volumePricing}
      />

      <CreateVolumeModal
        mode="clone"
        isOpen={cloneModalOpen}
        onClose={() => {
          setCloneModalOpen(false);
          setCloneSourceVolume(null);
          logUserAction('clone_volume_modal_closed');
        }}
        onClone={handleCloneVolume}
        sourceVolume={cloneSourceVolume}
        regions={regions}
        images={osImages}
        existingVolumeNames={getFilteredVolumeNames(volumes, selectedRegion)}
        selectedRegion={selectedRegion}
        volumePricing={volumePricing}
      />

      <CreateVolumeModal
        mode="resize"
        isOpen={resizeModalOpen}
        onClose={() => {
          setResizeModalOpen(false);
          setResizeSourceVolume(null);
          logUserAction('resize_volume_modal_closed');
        }}
        onResize={handleResizeVolume}
        sourceVolume={resizeSourceVolume}
        regions={regions}
        images={osImages}
        existingVolumeNames={getFilteredVolumeNames(volumes, selectedRegion)}
        selectedRegion={selectedRegion}
        volumePricing={volumePricing}
      />

      <VolumeInfoModal
        isOpen={infoModal.open}
        onClose={() => setInfoModal({ open: false, volume: null })}
        volume={infoModal.volume}
        onResize={handleResizeClick}
        onClone={handleCloneClick}
        isResizing={loadingStates.resizing && loadingStates.resizing.has(infoModal.volume?.id)}
        isCloning={loadingStates.cloning && loadingStates.cloning.size > 0}
      />

      {/* Delete Confirmation Modal */}
{deleteModal.open && deleteModal.volume && (
  <div style={{
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px'
  }}>
    <div style={{
      backgroundColor: '#121212',
      border: '1px solid #333333',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '420px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        borderBottom: '1px solid #2a2a2a'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff7777">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
          </svg>
          <h3 style={{
            fontWeight: '600',
            color: '#fff',
            margin: 0,
            fontSize: '16px'
          }}>
            Delete Volume
          </h3>
        </div>
        <button
          onClick={() => {
            setDeleteModal({ open: false, volume: null });
            logUserAction('delete_volume_modal_closed');
          }}
          disabled={isDeleting}
          style={{
            padding: '8px',
            borderRadius: '6px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#6b7280',
            cursor: isDeleting ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div style={{ padding: '20px' }}>
        <p style={{
          color: '#9ca3af',
          marginBottom: '16px',
          marginTop: 0,
          fontSize: '14px',
          lineHeight: '1.6'
        }}>
          Are you sure you want to delete this volume?
        </p>
        
        {/* Volume Name Box */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #2a2a2a',
          borderRadius: '8px',
          padding: '12px 14px',
          marginBottom: '16px'
        }}>
          <span style={{
            color: '#6b7280',
            fontSize: '11px',
            display: 'block',
            marginBottom: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Volume Name
          </span>
          <span style={{
            color: '#e0eacc',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {sanitizeText(getVolumeDisplayName(deleteModal.volume))}
          </span>
        </div>
        
        {/* Warning Box */}
        <div style={{
          backgroundColor: 'rgba(255,119,119,0.05)',
          border: '1px solid rgba(255,119,119,0.2)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff8080" style={{ flexShrink: 0 }}>
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          <span style={{ color: '#ff8080', fontSize: '12px' }}>
            This action cannot be undone.
          </span>
        </div>
        
        {/* Delete Button */}
        <button
          onClick={handleDeleteConfirm}
          disabled={isDeleting}
          style={{
            width: '100%',
            backgroundColor: isDeleting ? '#4a3030' : 'rgba(255,100,100,0.15)',
            border: '1px solid rgba(255,119,119,0.4)',
            color: '#ff7777',
            borderRadius: '8px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 20px',
            fontSize: '14px',
            cursor: isDeleting ? 'not-allowed' : 'pointer',
            gap: '8px'
          }}
        >
          {isDeleting ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid transparent',
                borderTopColor: '#ff7777',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Deleting...
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
              Delete Volume
            </>
          )}
        </button>
      </div>
    </div>
  </div>
)}
      <VolumeAttachModal
        isOpen={attachModal.open}
        onClose={() => {
          setAttachModal({ open: false, volume: null });
          logUserAction('attach_volume_modal_closed');
        }}
        onAttach={handleAttachConfirm}
        volume={attachModal.volume || {}}
        vms={vms}
        isAttaching={loadingStates.attaching.size > 0}
        isLoadingVms={loadingStates.vms}
      />

      {/* Detach Confirmation Modal */}
      {detachConfirmModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-md border border-[#333333] shadow-md w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-white text-xl">Detach Volume</h3>
              <button
                onClick={() => {
                  setDetachConfirmModal({ open: false, volume: null });
                  logUserAction('detach_volume_modal_closed');
                }}
                className="text-gray-400 hover:text-white transition-colors bg-transparent border-none cursor-pointer"
              >
                <FaTimes />
              </button>
            </div>
            
            {detachConfirmModal.volume && detachConfirmModal.volume.vm_status && 
             detachConfirmModal.volume.vm_status !== 'SHUTOFF' ? (
              <div className="bg-[#331111] p-4 rounded-md mb-4">
                <p className="text-red-300 flex items-start text-sm">
                  <FaExclamationTriangle className="mt-1 mr-2 flex-shrink-0" />
                  <span>
                    <strong>Cannot detach:</strong> Volume is attached to a VM with status <strong>{sanitizeText(detachConfirmModal.volume.vm_status)}</strong>. 
                    The VM must be in <strong>SHUTOFF</strong> state to perform volume operations.
                  </span>
                </p>
              </div>
            ) : (
              <div className="bg-[#142218] p-4 rounded-md mb-4">
                <p className="text-[#e0eacc] flex items-start text-sm">
                  <FaExclamationTriangle className="mt-1 mr-2 flex-shrink-0" />
                  <span>
                    Are you sure you want to detach volume <strong>{sanitizeText(getVolumeDisplayName(detachConfirmModal.volume))}</strong>? 
                    This action may disrupt applications using this volume.
                  </span>
                </p>
              </div>
            )}
            
            <div className="flex gap-3 mt-6 justify-end">
              <button
                onClick={() => {
                  setDetachConfirmModal({ open: false, volume: null });
                  logUserAction('detach_volume_cancelled');
                }}
                className="bg-[#333333] hover:bg-[#444444] text-white rounded-md transition-colors px-4 py-2"
              >
                Cancel
              </button>
              
              {!(detachConfirmModal.volume && detachConfirmModal.volume.vm_status && 
                detachConfirmModal.volume.vm_status !== 'SHUTOFF') && (
                <button
                  onClick={handleDetachConfirm}
                  className="bg-[#e0eacc] hover:bg-[#c8d9a9] text-black rounded-md font-medium flex items-center justify-center px-4 py-2 min-w-[80px]"
                >
                  Detach
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status CSS */}
      <style jsx>{`
        .status-badge {
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
          display: inline-block;
        }
        
        .status-available {
          background: rgba(202, 217, 238, 0.1);
          color: #cad9ee;
          border: 1px solid rgba(202, 217, 238, 0.3);
        }
        
        .status-attached {
          background: rgba(202, 217, 238, 0.15);
          color: #cad9ee;
          border: 1px solid #cad9ee;
        }
        
        .status-creating {
          background: rgba(202, 217, 238, 0.1);
          color: #cad9ee;
          border: 1px solid rgba(202, 217, 238, 0.4);
          animation: pulse-creating 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes pulse-creating {
          0% {
            background: rgba(202, 217, 238, 0.1);
            border-color: rgba(202, 217, 238, 0.4);
            box-shadow: 0 0 0 0 rgba(202, 217, 238, 0.4);
          }
          50% {
            background: rgba(202, 217, 238, 0.2);
            border-color: rgba(202, 217, 238, 0.6);
            box-shadow: 0 0 0 4px rgba(202, 217, 238, 0.1);
          }
          100% {
            background: rgba(202, 217, 238, 0.1);
            border-color: rgba(202, 217, 238, 0.4);
            box-shadow: 0 0 0 0 rgba(202, 217, 238, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default VolumesPage;